import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AdminDashboardLayout from "../../layouts/AdminDashboardLayout";
import BrandLoader from "../../components/BrandLoader";
import adminApi from "../../services/adminApi";
import { liveSessionAPI, notificationAPI } from "../../services/api";
import { useDynamicPackages } from "../../hooks/useDynamicPackages";
import {
  FiPlus, FiSearch, FiDownload, FiEdit3, FiTrash2,
  FiX, FiInfo, FiBell, FiUsers, FiClock, FiCalendar,
  FiCheckCircle, FiTrendingUp, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight,
  FiVideo, FiRadio, FiCopy, FiShare2, FiLink, FiChevronDown, FiCheck, FiFilter, FiAlertTriangle, FiActivity
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { GiLotus } from "react-icons/gi";

const T = {
  olive:"#6B8A5A", olive2:"#8BA47A",
  orange:"#F29A0C", orange2:"#E97818",
  dark:"#1F1F1F", mid:"#555", muted:"#AAA", light:"#CCC",
  border:"rgba(239,229,216,0.8)",
  green:"#22C55E", greenDk:"#16A34A",
  red:"#E24B4A", amber:"#F59E0B", blue:"#185FA5",
};
const glass = {
  background:"rgba(255,255,255,0.82)",
  border:"1px solid rgba(255,255,255,0.95)",
  backdropFilter:"blur(32px)", WebkitBackdropFilter:"blur(32px)",
  boxShadow:"0 20px 60px rgba(0,0,0,0.06),inset 0 1px 0 #fff",
};
const STATUS_S = {
  Live:      { bg:"rgba(34,197,94,0.12)",  c:"#16A34A", dot:"#22C55E" },
  Upcoming:  { bg:"rgba(24,95,165,0.10)",  c:"#185FA5", dot:"#378ADD" },
  Completed: { bg:"rgba(107,138,90,0.10)", c:"#27500A", dot:"#6B8A5A" },
  Cancelled: { bg:"rgba(226,75,74,0.10)",  c:"#E24B4A", dot:"#F87171" },
};
const TIER_S = {
  All:       { bg:"rgba(107,138,90,0.10)", c:"#27500A", b:"rgba(107,138,90,0.25)" },
  Free:      { bg:"rgba(200,200,200,0.12)", c:"#666",    b:"rgba(200,200,200,0.3)"  },
  Basic:     { bg:"rgba(24,95,165,0.10)",  c:"#0C447C", b:"rgba(24,95,165,0.20)" },
  Standard:  { bg:"rgba(83,74,183,0.10)",  c:"#3C3489", b:"rgba(83,74,183,0.20)" },
  Premium:   { bg:"rgba(242,154,12,0.12)", c:"#C8660A", b:"rgba(242,154,12,0.25)" },
  Pro:       { bg:"rgba(107,138,90,0.12)", c:"#27500A", b:"rgba(107,138,90,0.25)" },
};
const TIERS = ["Free","Basic","Standard","Premium","Pro"];
const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

// ─── TIER RANK HIERARCHY EVALUATOR (With Dynamic DB Custom Tier Matching) ─────
const getTierRank = (tierStr, packageList = []) => {
  if (!tierStr) return 0;
  const t = String(tierStr).trim().toLowerCase();
  if (t === "free" || t === "free trial" || t === "trial" || t === "all" || t === "all users") return 0;

  // 1. Dynamic DB package match (reads custom accessLevel set by Super Admin)
  if (Array.isArray(packageList) && packageList.length > 0) {
    const matched = packageList.find(p => 
      (p.name && p.name.trim().toLowerCase() === t) ||
      (p.packageType && p.packageType.trim().toLowerCase() === t)
    );
    if (matched && typeof matched.accessLevel === "number") {
      return matched.accessLevel;
    }
  }

  // 2. Canonical standard keyword fallback
  if (t.includes("basic") || t.includes("bronze")) return 1;
  if (t.includes("standard") || t.includes("silver")) return 2;
  if (t.includes("premium") || t.includes("gold")) return 3;
  if (t.includes("pro") || t.includes("ytt") || t.includes("vip")) return 4;
  return 1;
};

// ─── CONFIRMATION MODAL ───────────────────────────────────────────────────────
function ConfirmModal({ isOpen, title, message, confirmText = "Delete", cancelText = "Cancel", isDanger = true, onConfirm, onCancel }) {
  if (!isOpen) return null;
  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-[400] flex items-center justify-center p-4"
      style={{ background:"rgba(0,0,0,0.5)", backdropFilter:"blur(8px)", WebkitBackdropFilter:"blur(8px)" }}
      onClick={onCancel}>
      <motion.div initial={{ scale:0.9, opacity:0, y:20 }} animate={{ scale:1, opacity:1, y:0 }} exit={{ scale:0.9, opacity:0, y:20 }}
        onClick={e => e.stopPropagation()}
        className="rounded-[28px] p-6 max-w-sm w-full text-center relative overflow-hidden"
        style={{ background:"rgba(255,255,255,0.98)", border:"1px solid rgba(255,255,255,0.9)", boxShadow:"0 30px 80px rgba(0,0,0,0.25)" }}>
        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ background: isDanger ? "rgba(226,75,74,0.12)" : "rgba(107,138,90,0.12)" }}>
          {isDanger ? <FiTrash2 size={24} style={{ color: T.red }}/> : <FiInfo size={24} style={{ color: T.olive }}/>}
        </div>
        <h3 className="font-black text-[18px] text-[#1F1F1F] mb-1.5" style={{ fontFamily: "'Poppins',sans-serif" }}>{title}</h3>
        <p className="text-[13px] mb-6 text-[#666] leading-relaxed">{message}</p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 h-11 rounded-full text-[13px] font-bold border cursor-pointer active:scale-95 transition-all"
            style={{ background: "#fff", borderColor: "rgba(239,229,216,0.9)", color: T.mid }}>
            {cancelText}
          </button>
          <button onClick={onConfirm} className="flex-1 h-11 rounded-full text-[13px] font-bold text-white border-none cursor-pointer active:scale-95 transition-all"
            style={{ background: isDanger ? "linear-gradient(135deg,#e24b4a,#dc2626)" : `linear-gradient(135deg,${T.olive},${T.olive2})`, boxShadow: isDanger ? "0 8px 20px rgba(226,75,74,0.35)" : "none" }}>
            {confirmText}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── CUSTOM DROPDOWN COMPONENT (With Search & Dynamic Height) ───────────────────
function CustomDropdown({ value, onChange, options, label, icon: Icon, fullWidth = false, align = "left", searchable = true, placeholder = "Select option" }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) setSearchTerm("");
  }, [open]);

  const selectedOption = (value !== undefined && value !== null && String(value) !== "")
    ? options.find((o) => String(o.value) === String(value))
    : null;

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    return options.filter((o) =>
      String(o.label).toLowerCase().includes(searchTerm.toLowerCase().trim())
    );
  }, [options, searchTerm]);

  return (
    <div 
      className={`relative ${fullWidth ? "w-full" : "inline-block"} text-left`} 
      style={{ zIndex: open ? 999 : 20 }}
      ref={dropdownRef}
    >
      <motion.button
        type="button"
        whileTap={{ scale: 0.98 }}
        onClick={() => setOpen((prev) => !prev)}
        className={`flex items-center justify-between gap-2 ${fullWidth ? "w-full rounded-[13px] px-3.5 py-2.5 text-[13px]" : "rounded-full px-4 py-2 text-[12px]"} font-semibold border cursor-pointer outline-none transition-all shadow-sm`}
        style={{
          background: open ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.85)",
          borderColor: open ? T.olive : "rgba(239,229,216,0.8)",
          color: selectedOption ? "#1F1F1F" : T.mid,
          boxShadow: open ? "0 4px 20px rgba(107,138,90,0.18)" : "none",
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <span className="flex items-center gap-2 truncate min-w-0">
          {Icon && <Icon size={14} style={{ color: open ? T.olive : T.muted }} className="flex-shrink-0" />}
          <span className="truncate">{selectedOption ? selectedOption.label : (placeholder || label)}</span>
        </span>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex items-center justify-center ml-0.5 flex-shrink-0"
        >
          <FiChevronDown size={14} style={{ color: open ? T.olive : T.muted }} />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.95 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className={`ad-dd-menu absolute ${align === "right" ? "right-0" : "left-0"} mt-1.5 ${fullWidth ? "w-full min-w-[180px]" : "min-w-[240px] max-w-[320px]"} rounded-[20px] p-2 shadow-2xl text-left flex flex-col`}
            style={{
              background: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              border: "1px solid rgba(239,229,216,0.9)",
              boxShadow: "0 16px 40px rgba(0,0,0,0.16)",
              maxHeight: "340px",
              zIndex: 1000,
            }}
          >
            {/* Search Bar inside Dropdown Popover */}
            {searchable && options.length > 3 && (
              <div className="p-1 mb-1.5 border-b border-zinc-100/80 sticky top-0 bg-white/90 backdrop-blur-md z-10">
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-zinc-100/90 border border-zinc-200/70 focus-within:border-[#6B8A5A] transition-all">
                  <FiSearch size={13} className="text-zinc-400 shrink-0" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search options..."
                    className="w-full text-[11.5px] bg-transparent outline-none text-zinc-800 placeholder:text-zinc-400 font-medium"
                    onClick={(e) => e.stopPropagation()}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setSearchTerm(""); }}
                      className="text-zinc-400 hover:text-zinc-600 bg-transparent border-none cursor-pointer p-0 flex items-center justify-center"
                    >
                      <FiX size={12} />
                    </button>
                  )}
                </div>
              </div>
            )}

            {label && (
              <div className="px-2 py-1 mb-1 text-[9px] font-black uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                {label}
              </div>
            )}

            <div className="overflow-y-auto flex-1 max-h-[260px] pr-0.5" style={{ scrollbarWidth: "thin", msOverflowStyle: "none" }}>
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-4 text-center text-[11.5px] text-zinc-400 italic">
                  No matching items found
                </div>
              ) : (
                filteredOptions.map((opt) => {
                  const isSelected = String(opt.value) === String(value);
                  return (
                    <motion.button
                      key={String(opt.value)}
                      type="button"
                      whileHover={{ x: 2, background: "rgba(107,138,90,0.08)" }}
                      onClick={() => {
                        onChange(opt.value);
                        setOpen(false);
                      }}
                      className="w-full flex items-center justify-between px-3 py-2 text-[12px] font-semibold rounded-[12px] border-none bg-transparent cursor-pointer transition-all mb-0.5 text-left"
                      style={{
                        color: isSelected ? T.olive : "#333",
                        fontFamily: "'Inter', sans-serif",
                        background: isSelected ? "rgba(107,138,90,0.1)" : "transparent",
                      }}
                    >
                      <span className="flex items-center gap-2 truncate min-w-0">
                        {opt.dotColor && (
                          <span
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ background: opt.dotColor }}
                          />
                        )}
                        <span className="truncate">{opt.label}</span>
                      </span>
                      {isSelected && <FiCheck size={13} style={{ color: T.olive }} className="flex-shrink-0 ml-1" />}
                    </motion.button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── CUSTOM DATE PICKER COMPONENT ─────────────────────────────────────────────
function CustomDatePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const initialDate = useMemo(() => {
    if (value) {
      const parts = value.split("-").map(Number);
      if (parts.length === 3) return new Date(parts[0], parts[1] - 1, parts[2]);
    }
    return new Date();
  }, [value]);

  const [viewYear, setViewYear] = useState(initialDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialDate.getMonth());

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();

  const prevMonth = () => {
    const prevDate = new Date(viewYear, viewMonth - 1, 1);
    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    if (prevDate < currentMonthStart) return;
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else { setViewMonth(m => m - 1); }
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else { setViewMonth(m => m + 1); }
  };

  const selectDay = (day) => {
    const mm = String(viewMonth + 1).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    const cellDate = new Date(viewYear, viewMonth, day);
    cellDate.setHours(0, 0, 0, 0);
    if (cellDate < today) return;
    onChange(`${viewYear}-${mm}-${dd}`);
    setOpen(false);
  };

  const formattedDisplay = useMemo(() => {
    if (!value) return "Select date";
    const parts = value.split("-").map(Number);
    if (parts.length !== 3) return value;
    const d = new Date(parts[0], parts[1] - 1, parts[2]);
    return d.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
  }, [value]);

  return (
    <div className="relative w-full text-left" style={{ zIndex: open ? 999 : 20 }} ref={ref}>
      <motion.button
        type="button"
        whileTap={{ scale: 0.98 }}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 rounded-[13px] px-3.5 py-2.5 text-[13px] font-semibold border cursor-pointer outline-none transition-all shadow-sm"
        style={{
          background: open ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.85)",
          borderColor: open ? T.olive : "rgba(239,229,216,0.8)",
          color: value ? "#1F1F1F" : T.mid,
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <span className="flex items-center gap-2 truncate">
          <FiCalendar size={14} style={{ color: open ? T.olive : T.muted }} />
          <span>{formattedDisplay}</span>
        </span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <FiChevronDown size={14} style={{ color: open ? T.olive : T.muted }} />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 mt-1.5 w-72 rounded-[20px] p-3.5 shadow-2xl text-left"
            style={{
              background: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              border: "1px solid rgba(239,229,216,0.9)",
              boxShadow: "0 20px 48px rgba(0,0,0,0.16)",
              zIndex: 1000,
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="text-[13px] font-extrabold text-zinc-800">
                {MONTHS[viewMonth]} {viewYear}
              </span>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={prevMonth}
                  disabled={new Date(viewYear, viewMonth - 1, 1) < new Date(today.getFullYear(), today.getMonth(), 1)}
                  className="w-7 h-7 rounded-lg flex items-center justify-center border-none bg-zinc-100 hover:bg-zinc-200 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <FiChevronLeft size={14} />
                </button>
                <button type="button" onClick={nextMonth} className="w-7 h-7 rounded-lg flex items-center justify-center border-none bg-zinc-100 hover:bg-zinc-200 cursor-pointer">
                  <FiChevronRight size={14} />
                </button>
              </div>
            </div>

            {/* Weekday Labels */}
            <div className="grid grid-cols-7 gap-1 text-center mb-1">
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(d => (
                <span key={d} className="text-[10px] font-black uppercase text-zinc-400">{d}</span>
              ))}
            </div>

            {/* Days Grid */}
            <div className="grid grid-cols-7 gap-1 text-center">
              {Array.from({ length: firstDayOfWeek }).map((_, i) => (
                <span key={`empty-${i}`} />
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const dayNum = i + 1;
                const mm = String(viewMonth + 1).padStart(2, "0");
                const dd = String(dayNum).padStart(2, "0");
                const dateStr = `${viewYear}-${mm}-${dd}`;
                const cellDate = new Date(viewYear, viewMonth, dayNum);
                cellDate.setHours(0, 0, 0, 0);
                const isPast = cellDate < today;
                const isSelected = value === dateStr;

                return (
                  <button
                    key={dayNum}
                    type="button"
                    disabled={isPast}
                    onClick={() => !isPast && selectDay(dayNum)}
                    className="w-8 h-8 rounded-full text-[12px] font-bold flex items-center justify-center border-none transition-all m-auto"
                    style={{
                      background: isSelected ? T.olive : "transparent",
                      color: isSelected ? "#fff" : isPast ? "#ccc" : "#333",
                      boxShadow: isSelected ? "0 4px 12px rgba(107,138,90,0.4)" : "none",
                      opacity: isPast ? 0.35 : 1,
                      cursor: isPast ? "not-allowed" : "pointer",
                    }}
                  >
                    {dayNum}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── CUSTOM SUB NUMBER DROPDOWN FOR TIME PICKER ──────────────────────────────
function CustomSubNumberDropdown({ value, options, onChange, label, hideDisplayButton = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div className="relative" ref={dropRef}>
      <motion.button
        type="button"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center p-1 rounded-lg border-none text-[12px] font-extrabold cursor-pointer outline-none transition-all"
        style={{
          background: isOpen ? "rgba(107,138,90,0.15)" : "transparent",
          color: T.olive,
        }}
        title={`Open ${label || "options"}`}
      >
        {!hideDisplayButton && <span className="mr-1">{String(value).padStart(2, "0")}</span>}
        <FiChevronDown size={14} style={{ color: T.olive, transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 4 }}
            transition={{ duration: 0.18 }}
            className="absolute left-0 top-[calc(100%+6px)] w-48 max-h-52 overflow-y-auto rounded-[18px] p-2 z-[2000]"
            style={{
              background: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              border: "1px solid rgba(239,229,216,0.9)",
              boxShadow: "0 14px 36px rgba(0,0,0,0.18)",
              scrollbarWidth: "thin",
            }}
          >
            {label && (
              <div className="px-2 py-1 text-[9px] font-black uppercase tracking-wider text-zinc-400 border-b border-zinc-100 mb-1">
                {label}
              </div>
            )}
            <div className="grid grid-cols-4 gap-1">
              {options.map((opt) => {
                const optVal = typeof opt === "object" ? opt.value : opt;
                const optLbl = typeof opt === "object" ? opt.label : String(opt).padStart(2, "0");
                const isSel = String(optVal) === String(value);

                return (
                  <motion.button
                    key={String(optVal)}
                    type="button"
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.92 }}
                    onClick={() => {
                      onChange(optVal);
                      setIsOpen(false);
                    }}
                    className="h-8 rounded-lg text-[11.5px] font-extrabold border-none cursor-pointer flex items-center justify-center transition-all"
                    style={{
                      background: isSel ? `linear-gradient(135deg, ${T.olive}, ${T.olive2})` : "rgba(107,138,90,0.06)",
                      color: isSel ? "#FFFFFF" : "#222",
                      boxShadow: isSel ? "0 3px 10px rgba(107,138,90,0.35)" : "none",
                    }}
                  >
                    {optLbl}
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── CUSTOM TIME PICKER COMPONENT ─────────────────────────────────────────────
function CustomTimePicker({ value, onChange, align = "right" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const parsed = useMemo(() => {
    let hour = 7;
    let min = "00";
    let period = "AM";
    if (value) {
      const parts = value.split(":");
      let h = parseInt(parts[0], 10);
      min = parts[1] || "00";
      if (!isNaN(h)) {
        period = h >= 12 ? "PM" : "AM";
        hour = h % 12 || 12;
      }
    }
    return { hour, min, period };
  }, [value]);

  const [inputHour, setInputHour] = useState(String(parsed.hour).padStart(2, "0"));
  const [inputMin, setInputMin] = useState(parsed.min);

  useEffect(() => {
    setInputHour(String(parsed.hour).padStart(2, "0"));
    setInputMin(parsed.min);
  }, [parsed]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateTime = (h, m, p) => {
    let hNum = parseInt(h, 10);
    if (isNaN(hNum)) hNum = 12;
    hNum = Math.min(12, Math.max(1, hNum));
    
    let h24 = hNum;
    if (p === "PM" && hNum < 12) h24 += 12;
    if (p === "AM" && hNum === 12) h24 = 0;
    const hh = String(h24).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    onChange(`${hh}:${mm}`);
  };

  const displayTime = value ? `${String(parsed.hour).padStart(2, "0")}:${parsed.min} ${parsed.period}` : "Select time";

  const PRESETS = [
    { label: "06:00 AM", h: 6, m: "00", p: "AM" },
    { label: "07:00 AM", h: 7, m: "00", p: "AM" },
    { label: "08:30 AM", h: 8, m: "30", p: "AM" },
    { label: "10:00 AM", h: 10, m: "00", p: "AM" },
    { label: "05:00 PM", h: 5, m: "00", p: "PM" },
    { label: "06:30 PM", h: 6, m: "30", p: "PM" },
    { label: "07:30 PM", h: 7, m: "30", p: "PM" },
  ];

  const all60Minutes = useMemo(() => Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0")), []);

  return (
    <div className="relative w-full text-left" style={{ zIndex: open ? 999 : 20 }} ref={ref}>
      <motion.button
        type="button"
        whileTap={{ scale: 0.98 }}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 rounded-[13px] px-3.5 py-2.5 text-[13px] font-semibold border cursor-pointer outline-none transition-all shadow-sm"
        style={{
          background: open ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.85)",
          borderColor: open ? T.olive : "rgba(239,229,216,0.8)",
          color: value ? "#1F1F1F" : T.mid,
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <span className="flex items-center gap-2 truncate">
          <FiClock size={14} style={{ color: open ? T.olive : T.muted }} />
          <span>{displayTime}</span>
        </span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <FiChevronDown size={14} style={{ color: open ? T.olive : T.muted }} />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={`absolute ${align === "right" ? "right-0" : "left-0"} mt-1.5 w-[310px] rounded-[20px] p-3.5 shadow-2xl text-left`}
            style={{
              background: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              border: "1px solid rgba(239,229,216,0.9)",
              boxShadow: "0 20px 48px rgba(0,0,0,0.16)",
              zIndex: 1000,
            }}
          >
            <div className="text-[10px] font-black uppercase tracking-wider text-zinc-400 mb-2 border-b pb-1">
              Select or Type Time (AM/PM)
            </div>

            {/* Custom Hour & Min Editable Inputs & Dropdowns */}
            <div className="flex items-center justify-between gap-2 mb-3 bg-zinc-50 p-2.5 rounded-xl border border-zinc-100">
              <div className="flex items-center gap-1.5">
                {/* Manual Hour Input + Dropdown */}
                <div className="flex items-center gap-1 bg-white px-2 py-1 rounded-xl border border-zinc-200 shadow-2xs">
                  <input
                    type="text"
                    maxLength={2}
                    value={inputHour}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "");
                      setInputHour(v);
                      if (v) {
                        const num = Math.min(12, Math.max(1, parseInt(v, 10)));
                        updateTime(num, parsed.min, parsed.period);
                      }
                    }}
                    onBlur={() => {
                      const num = parseInt(inputHour || "12", 10);
                      const safeH = isNaN(num) ? 12 : Math.min(12, Math.max(1, num));
                      const finalH = String(safeH).padStart(2, "0");
                      setInputHour(finalH);
                      updateTime(safeH, parsed.min, parsed.period);
                    }}
                    className="w-7 bg-transparent border-none outline-none font-black text-[13px] text-zinc-800 text-center"
                  />
                  <CustomSubNumberDropdown
                    label="Hour"
                    value={parsed.hour}
                    options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]}
                    onChange={(h) => {
                      setInputHour(String(h).padStart(2, "0"));
                      updateTime(h, parsed.min, parsed.period);
                    }}
                    hideDisplayButton={true}
                  />
                </div>

                <span className="font-bold text-zinc-400">:</span>

                {/* Manual Minute Input + Dropdown */}
                <div className="flex items-center gap-1 bg-white px-2 py-1 rounded-xl border border-zinc-200 shadow-2xs">
                  <input
                    type="text"
                    maxLength={2}
                    value={inputMin}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "");
                      setInputMin(v);
                      if (v) {
                        const num = Math.min(59, Math.max(0, parseInt(v, 10)));
                        const finalM = String(num).padStart(2, "0");
                        updateTime(parsed.hour, finalM, parsed.period);
                      }
                    }}
                    onBlur={() => {
                      const num = parseInt(inputMin || "0", 10);
                      const safeM = isNaN(num) ? 0 : Math.min(59, Math.max(0, num));
                      const finalM = String(safeM).padStart(2, "0");
                      setInputMin(finalM);
                      updateTime(parsed.hour, finalM, parsed.period);
                    }}
                    className="w-7 bg-transparent border-none outline-none font-black text-[13px] text-amber-700 text-center"
                  />
                  <CustomSubNumberDropdown
                    label="Minute (00-59)"
                    value={parsed.min}
                    options={all60Minutes}
                    onChange={(m) => {
                      setInputMin(m);
                      updateTime(parsed.hour, m, parsed.period);
                    }}
                    hideDisplayButton={true}
                  />
                </div>
              </div>

              {/* AM/PM Toggle Buttons */}
              <div className="flex border rounded-xl overflow-hidden border-zinc-200 flex-shrink-0 bg-white shadow-sm">
                {["AM", "PM"].map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => updateTime(parsed.hour, parsed.min, p)}
                    className="px-3 py-1.5 text-[12px] font-black cursor-pointer transition-colors border-none"
                    style={{
                      background: parsed.period === p ? T.olive : "transparent",
                      color: parsed.period === p ? "#fff" : "#666",
                    }}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Presets */}
            <div className="text-[9px] font-black uppercase tracking-wider text-zinc-400 mb-1.5">
              Quick Schedule Presets
            </div>
            <div className="flex flex-wrap gap-1">
              {PRESETS.map(pr => (
                <button
                  key={pr.label}
                  type="button"
                  onClick={() => {
                    updateTime(pr.h, pr.m, pr.p);
                    setOpen(false);
                  }}
                  className="px-2 py-1 text-[11px] font-semibold rounded-lg border cursor-pointer transition-all"
                  style={{
                    background: "rgba(107,138,90,0.06)",
                    borderColor: "rgba(107,138,90,0.2)",
                    color: T.olive,
                  }}
                >
                  {pr.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function formatSessionTimeWithZone(date) {
  const localTimeStr = date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
  let localTz = "";
  try {
    const parts = new Intl.DateTimeFormat([], { timeZoneName: "short" }).formatToParts(date);
    const tzPart = parts.find(p => p.type === "timeZoneName");
    if (tzPart) localTz = tzPart.value;
  } catch (e) {
    console.error(e);
  }

  const istTimeStr = new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  }).format(date) + " IST";

  if (localTz && localTz !== "GMT+5:30" && localTz !== "IST") {
    return `${localTimeStr} ${localTz} (${istTimeStr})`;
  }
  return `${localTimeStr} IST`;
}

const getLocalDateString = (dateObj) => {
  if (!dateObj) return "";
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getLocal24HourTime = (dateObj) => {
  if (!dateObj) return "";
  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

const parseUtcDate = (dateStr) => {
  if (!dateStr) return new Date();
  if (!dateStr.endsWith("Z") && !dateStr.includes("+") && !dateStr.includes("-")) {
    return new Date(dateStr + "Z");
  }
  return new Date(dateStr);
};

/* ── Map API yoga class → UI session shape ── */
const mapClass = (c) => {
  const d = parseUtcDate(c.scheduledAt);
  const status = c.isLive ? "Live" : (c.isUpcoming ? "Upcoming" : "Completed");
  return {
    id:             c.id,
    title:          c.title,
    instructor:     c.instructorName || "Instructor TBA",
    instructorRaw:  c.instructorName || "",
    date:           d.toLocaleDateString("en-GB",{ day:"2-digit",month:"short",year:"numeric" }),
    dateObj:        d,
    dateStr:        getLocalDateString(d),
    day:            d.getDate(),
    time:           formatSessionTimeWithZone(d),
    duration:       c.durationInMinutes || 45,
    tier:           c.tier || "Free",
    status:         c.isActive === false ? "Cancelled" : status,
    capacity:       c.maxCapacity || 30,
    registered:     c.enrolledCount || 0,
    description:    c.description || "",
    notifyOnCreate: true,
    meetLink:       c.videoUrl || "",
    rosterNames:    c.rosterNames || [],
    rosterDetails:  c.rosterDetails || [],
  };
};

/* ── Check if session can be launched by Admin (Within 2 minutes before start time until end time) ── */
const getLaunchTimeStatus = (s) => {
  if (!s) return { canLaunch: false, message: "" };
  if (s.status === "Cancelled") return { canLaunch: false, message: "This session is cancelled." };
  if (s.status === "Completed") return { canLaunch: false, message: "This session has already ended." };

  const start = s.dateObj ? new Date(s.dateObj) : parseUtcDate(s.scheduledAt);
  const duration = s.duration || s.durationInMinutes || 45;
  const end = new Date(start.getTime() + duration * 60000);
  const now = new Date();

  // Allow launch starting 2 minutes before the scheduled start time
  const twoMinutesBefore = new Date(start.getTime() - 2 * 60000);

  if (now > end) {
    return { canLaunch: false, message: "This session has already ended." };
  }

  if (now < twoMinutesBefore) {
    const diffMs = start.getTime() - now.getTime();
    const diffMins = Math.max(1, Math.ceil(diffMs / 60000));
    const diffHours = Math.floor(diffMins / 60);
    const remMins = diffMins % 60;
    const timeStr = diffHours > 0 ? `${diffHours}h ${remMins}m` : `${diffMins} min`;
    return {
      canLaunch: false,
      startsIn: timeStr,
      scheduledTime: s.time || start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      message: `Session is scheduled for ${s.time || start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} (starts in ${timeStr}). Super Admin can launch the live studio 2 minutes before the scheduled start time. If you need to go live now, please prepone/edit the session time.`
    };
  }

  return { canLaunch: true, message: "Live studio ready to launch!" };
};

const parseAddressDetails = (address) => {
  if (!address) {
    return { place: "India", state: "Delhi", country: "India" };
  }

  const cleanAddr = address.trim();
  const lowerAddr = cleanAddr.toLowerCase();

  // 1. Identify Country
  let country = "India";
  const countries = ["india", "usa", "united states", "uk", "united kingdom", "canada", "australia", "germany", "singapore", "dubai", "uae"];
  for (const c of countries) {
    if (lowerAddr.includes(c)) {
      country = c === "usa" ? "USA" : c === "uk" ? "UK" : c === "uae" ? "UAE" : c.split(" ").map(w => w[0].toUpperCase() + w.slice(1)).join(" ");
      break;
    }
  }

  // 2. Identify Indian State / Union Territory
  let state = "Karnataka";
  const statesMap = {
    "andhra pradesh": "Andhra Pradesh", "arunachal pradesh": "Arunachal Pradesh", "assam": "Assam", "bihar": "Bihar",
    "chhattisgarh": "Chhattisgarh", "goa": "Goa", "gujarat": "Gujarat", "haryana": "Haryana", "himachal pradesh": "Himachal Pradesh",
    "jharkhand": "Jharkhand", "karnataka": "Karnataka", "kerala": "Kerala", "madhya pradesh": "Madhya Pradesh",
    "maharashtra": "Maharashtra", "manipur": "Manipur", "meghalaya": "Meghalaya", "mizoram": "Mizoram", "nagaland": "Nagaland",
    "odisha": "Odisha", "punjab": "Punjab", "rajasthan": "Rajasthan", "sikkim": "Sikkim", "tamil nadu": "Tamil Nadu",
    "telangana": "Telangana", "tripura": "Tripura", "uttar pradesh": "Uttar Pradesh", "uttarakhand": "Uttarakhand",
    "west bengal": "West Bengal", "delhi": "Delhi", "jammu & kashmir": "Jammu & Kashmir", "jammu and kashmir": "Jammu & Kashmir",
    "ladakh": "Ladakh", "puducherry": "Puducherry", "chandigarh": "Chandigarh", "dadra and nagar haveli": "Dadra & Nagar Haveli",
    "daman and diu": "Daman & Diu", "lakshadweep": "Lakshadweep", "andaman and nicobar": "Andaman & Nicobar"
  };

  const abbreviations = {
    "up": "Uttar Pradesh", "mp": "Madhya Pradesh", "ap": "Andhra Pradesh", "hp": "Himachal Pradesh",
    "mh": "Maharashtra", "ka": "Karnataka", "kl": "Kerala", "tn": "Tamil Nadu", "dl": "Delhi",
    "hr": "Haryana", "pb": "Punjab", "rj": "Rajasthan", "wb": "West Bengal", "uk": "Uttarakhand",
    "jk": "Jammu & Kashmir", "br": "Bihar", "jh": "Jharkhand", "gj": "Gujarat", "as": "Assam"
  };

  let foundState = false;
  for (const [key, val] of Object.entries(statesMap)) {
    if (lowerAddr.includes(key)) {
      state = val;
      foundState = true;
      break;
    }
  }

  if (!foundState) {
    const words = lowerAddr.split(/[\s,]+/);
    for (const [key, val] of Object.entries(abbreviations)) {
      if (words.includes(key)) {
        state = val;
        foundState = true;
        break;
      }
    }
  }

  // 3. Identify Place (City/Town/District)
  const parts = cleanAddr.split(",").map(p => p.trim());
  let place = parts[0] || "Not recorded";

  if (parts.length > 1) {
    const cityCandidates = parts.filter(p => {
      const lp = p.toLowerCase();
      if (statesMap[lp] || abbreviations[lp] || lp === "india" || /^\d+$/.test(p)) return false;
      return true;
    });
    if (cityCandidates.length > 0) {
      place = cityCandidates[cityCandidates.length - 1];
    }
  }

  return { place, state, country };
};

const StatusPill = ({ status }) => {
  const s = STATUS_S[status] || STATUS_S.Upcoming;
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full"
      style={{ background:s.bg,color:s.c }}>
      <span className="w-[5px] h-[5px] rounded-full flex-shrink-0"
        style={{ background:s.dot,animation:status==="Live"?"blink 2s infinite":undefined }}/>
      {status}
    </span>
  );
};
const TierPill = ({ tier }) => {
  const s = TIER_S[tier] || TIER_S.All;
  return (
    <span className="inline-flex items-center text-[10px] font-black px-2.5 py-1 rounded-full"
      style={{ background:s.bg,color:s.c,border:`1px solid ${s.b}` }}>{tier}</span>
  );
};
const StatCard = ({ icon:Icon,value,label,trend,iconBg,accentColor,cardBg,delay=0 }) => (
  <motion.div initial={{ opacity:0,y:16 }} animate={{ opacity:1,y:0 }} transition={{ delay }}
    whileHover={{ y:-4,boxShadow:"0 22px 50px rgba(0,0,0,0.1)" }}
    className="ad-kpi-card ad-kpi-flat relative overflow-hidden rounded-[20px] p-4 border"
    style={{ background:cardBg||"rgba(255,255,255,0.85)",borderColor:"rgba(255,255,255,0.9)",transition:"all 0.28s" }}>
    <div className="ad-kpi-icon w-9 h-9 rounded-[11px] flex items-center justify-center mb-3" style={{ background:iconBg }}>
      <Icon size={16} style={{ color:"#fff" }}/>
    </div>
    <p className="ad-kpi-val font-black text-[#1F1F1F] mb-1" style={{ fontFamily:"'Poppins',sans-serif",fontSize:24,letterSpacing:"-1px",lineHeight:1 }}>{value}</p>
    <p className="ad-kpi-lbl text-[10px] font-bold uppercase tracking-[0.8px] mb-1.5" style={{ color:T.muted }}>{label}</p>
    <p className="ad-kpi-note text-[11px] font-bold flex items-center gap-1" style={{ color:T.greenDk }}>
      <FiTrendingUp size={11}/>{trend}
    </p>
    <div className="absolute bottom-0 left-0 right-0 h-[3px] rounded-b-[20px]" style={{ background:accentColor }}/>
  </motion.div>
);

/* ── SESSION FORM MODAL   wired to API with strict validation & default empty states ── */
const SessionModal = ({ session, onClose, onSave, showToast, existingSessions = [] }) => {
  const { tiers: dynamicTiers } = useDynamicPackages();
  const isEdit = !!session;
  const [notify, setNotify] = useState(true);
  
  const [title, setTitle] = useState(session?.title || "");
  const [instructor, setInstructor] = useState(session ? (session.instructorRaw ?? session.instructor ?? "") : "");
  const [tierState, setTierState] = useState(session?.tier || "");
  const [dateState, setDateState] = useState(session?.dateObj ? getLocalDateString(session.dateObj) : "");
  const [timeState, setTimeState] = useState(session?.dateObj ? getLocal24HourTime(session.dateObj) : "");
  const [durationState, setDurationState] = useState(session?.duration ? String(session.duration) : "");
  const [capacityState, setCapacityState] = useState(session?.capacity ? String(session.capacity) : "");
  const [categoryState, setCategoryState] = useState(session?.category || "");
  const [descriptionState, setDescriptionState] = useState(session?.description || "");
  
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateTitle = (val) => {
    const v = (val || "").trim();
    if (!v) return "Session title is required.";
    if (v.length < 3) return "Title must be at least 3 characters.";
    if (v.length > 100) return "Title cannot exceed 100 characters.";
    if (/<script|javascript:/i.test(v)) return "Title contains forbidden script tags.";
    if (!/^[a-zA-Z0-9\s]+$/.test(v)) return "Title cannot contain special characters. Only alphanumeric characters and spaces are allowed.";
    return "";
  };

  const validateInstructor = (val) => {
    const v = (val || "").trim();
    if (!v) return "Instructor name is required. Please select or enter an instructor.";
    if (v.length < 2) return "Instructor name must be at least 2 characters.";
    if (v.length > 100) return "Instructor name cannot exceed 100 characters.";
    if (!/^[a-zA-Z\s.'\-]+$/.test(v)) return "Instructor name can only contain letters, spaces, dots, and hyphens.";
    return "";
  };

  const validateTier = (val) => {
    const v = (val || "").trim();
    if (!v) return "Tier eligibility is required. Please select a package tier.";
    return "";
  };

  const validateDate = (val) => {
    const v = (val || "").trim();
    if (!v) return "Date is required. Please select a session date.";
    const parts = v.split("-").map(Number);
    if (parts.length !== 3) return "Invalid date format.";
    const selected = new Date(parts[0], parts[1] - 1, parts[2]);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selected < today) return "Cannot select a past date. Please select today or a future date.";
    return "";
  };

  const validateTime = (val, currentSelectedDate = dateState) => {
    const v = (val || "").trim();
    if (!v) return "Time is required. Please select a session start time.";
    if (currentSelectedDate) {
      const parts = currentSelectedDate.split("-").map(Number);
      if (parts.length === 3) {
        const selDate = new Date(parts[0], parts[1] - 1, parts[2]);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selDate.getTime() === today.getTime()) {
          const timeParts = v.split(":").map(Number);
          if (timeParts.length === 2) {
            const now = new Date();
            const chosenTime = new Date();
            chosenTime.setHours(timeParts[0], timeParts[1], 0, 0);
            if (chosenTime <= now) {
              return "Time cannot be in the past for today's date.";
            }
          }
        }
      }
    }
    return "";
  };

  const validateDuration = (val) => {
    const v = (val || "").trim();
    if (!v) return "Duration is required. Please select a session duration.";
    return "";
  };

  const validateCapacity = (val) => {
    const v = String(val || "").trim();
    if (!v) return "Max capacity is required.";
    const num = parseInt(v, 10);
    if (isNaN(num) || num <= 0 || num > 10000) return "Capacity must be a number between 1 and 10,000.";
    return "";
  };

  const validateCategory = (val) => {
    const v = (val || "").trim();
    if (!v) return "Session category is required.";
    if (v.length < 2) return "Category must be at least 2 characters.";
    if (v.length > 100) return "Category cannot exceed 100 characters.";
    if (!/^[a-zA-Z0-9\s\-&]+$/.test(v)) return "Category can only contain letters, numbers, spaces, hyphens, and ampersands.";
    return "";
  };

  const validateDescription = (val) => {
    if (!val) return "";
    if (val.length > 2000) return "Description cannot exceed 2000 characters.";
    if (/<script|javascript:/i.test(val)) return "Description contains forbidden script tags.";
    return "";
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    let err = "";
    if (field === "title") err = validateTitle(title);
    if (field === "instructor") err = validateInstructor(instructor);
    if (field === "tier") err = validateTier(tierState);
    if (field === "date") err = validateDate(dateState);
    if (field === "time") err = validateTime(timeState);
    if (field === "duration") err = validateDuration(durationState);
    if (field === "capacity") err = validateCapacity(capacityState);
    if (field === "category") err = validateCategory(categoryState);
    if (field === "description") err = validateDescription(descriptionState);
    setErrors(prev => ({ ...prev, [field]: err }));
  };

  const handleSave = async () => {
    const tErr = validateTitle(title);
    const iErr = validateInstructor(instructor);
    const tierErr = validateTier(tierState);
    const dErr = validateDate(dateState);
    const timeErr = validateTime(timeState, dateState);
    const durErr = validateDuration(durationState);
    const capErr = validateCapacity(capacityState);
    const catErr = validateCategory(categoryState);
    const descErr = validateDescription(descriptionState);

    const newErrors = {
      title: tErr,
      instructor: iErr,
      tier: tierErr,
      date: dErr,
      time: timeErr,
      duration: durErr,
      capacity: capErr,
      category: catErr,
      description: descErr
    };

    setErrors(newErrors);
    setTouched({
      title: true,
      instructor: true,
      tier: true,
      date: true,
      time: true,
      duration: true,
      capacity: true,
      category: true,
      description: true
    });

    if (tErr || iErr || tierErr || dErr || timeErr || durErr || capErr || catErr || descErr) {
      showToast("Please fix all validation errors before saving session.");
      return;
    }

    const scheduledAt = new Date(`${dateState}T${timeState}`);
    const reqStart = scheduledAt;
    const reqEnd = new Date(reqStart.getTime() + (parseInt(durationState, 10) || 45) * 60000);
    const reqTier = (tierState || "").trim().toLowerCase();

    // Frontend live overlap conflict check for same tier
    const conflict = (existingSessions || []).find(s => {
      if (session && session.id && String(s.id) === String(session.id)) return false;
      if (s.status === "Cancelled" || s.isActive === false) return false;

      const sTier = (s.tier || "Free").trim().toLowerCase();
      const isTierOverlap = sTier === reqTier || sTier === "all" || reqTier === "all";
      if (!isTierOverlap) return false;

      const sStart = s.dateObj ? new Date(s.dateObj) : (s.scheduledAt ? new Date(s.scheduledAt) : null);
      if (!sStart || isNaN(sStart.getTime())) return false;
      const sDur = parseInt(s.duration || s.durationInMinutes || 45, 10);
      const sEnd = new Date(sStart.getTime() + sDur * 60000);

      return reqStart < sEnd && reqEnd > sStart;
    });

    if (conflict) {
      const sStart = conflict.dateObj ? new Date(conflict.dateObj) : new Date(conflict.scheduledAt);
      const sDur = parseInt(conflict.duration || conflict.durationInMinutes || 45, 10);
      const sEnd = new Date(sStart.getTime() + sDur * 60000);
      const formatTime = (d) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
      const formatDate = (d) => d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });

      const overlapMsg = `A live session ('${conflict.title}') is already scheduled for ${conflict.tier} tier from ${formatTime(sStart)} to ${formatTime(sEnd)} on ${formatDate(sStart)}. Please choose a different time slot.`;
      setErrors(prev => ({ ...prev, time: overlapMsg }));
      showToast(overlapMsg);
      return;
    }

    const payload = {
      title: title.trim(),
      description: descriptionState.trim(),
      instructorName: instructor.trim(),
      scheduledAt: scheduledAt.toISOString(),
      durationInMinutes: parseInt(durationState) || 45,
      category: categoryState,
      tier: tierState,
      maxCapacity: parseInt(capacityState) || 30,
    };

    setSaving(true);
    try {
      if (session && session.id) {
        await liveSessionAPI.updateSession(session.id, payload);
        showToast("✓ Session updated successfully");
      } else {
        await liveSessionAPI.createSession(payload);
        showToast("✓ Session scheduled successfully — users notified");
      }
      onSave();
    } catch (e) {
      showToast(e.response?.data?.message || e.message || "Failed to save session");
    } finally {
      setSaving(false);
    }
  };

  const labelCls = "text-[10px] font-black uppercase tracking-[0.9px] block mb-1.5";

  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-[200] flex items-center justify-center"
      style={{ background:"rgba(15,15,15,0.28)",backdropFilter:"blur(6px)" }}
      onClick={onClose}>
      <motion.div initial={{ scale:0.96,opacity:0 }} animate={{ scale:1,opacity:1 }}
        exit={{ scale:0.96,opacity:0 }} transition={{ duration:0.3,ease:[0.25,1,0.5,1] }}
        onClick={e => e.stopPropagation()}
        className="ad-modal ad-pad relative rounded-[28px] p-8 overflow-y-auto"
        style={{ width:520,maxHeight:"90vh",background:"rgba(255,253,248,0.97)",
          boxShadow:"0 40px 100px rgba(0,0,0,0.18)",border:"1px solid rgba(255,255,255,0.9)",scrollbarWidth:"none" }}>
        <div className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage:"radial-gradient(circle,rgba(0,0,0,0.025) 1px,transparent 1px)",backgroundSize:"22px 22px",borderRadius:28 }}/>
        <div className="absolute top-0 right-0 w-52 h-52 rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(107,138,90,0.1),transparent)",filter:"blur(50px)" }}/>
        <div className="relative">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-[13px] flex items-center justify-center"
                style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`,boxShadow:`0 5px 14px rgba(107,138,90,0.4)` }}>
                <FiCalendar size={18} style={{ color:"#fff" }}/>
              </div>
              <div>
                <p className="font-black text-[16px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>
                  {isEdit?"Edit Session":"Schedule New Session"}
                </p>
                <p className="text-[11px] mt-0.5" style={{ color:T.muted }}>
                  {isEdit?"Update session details":"Create a new live session"}
                </p>
              </div>
            </div>
            <motion.button whileHover={{ background:"rgba(239,229,216,0.9)" }} onClick={onClose}
              className="w-8 h-8 rounded-[10px] flex items-center justify-center border-none cursor-pointer"
              style={{ background:"rgba(239,229,216,0.5)",color:"#888" }}>
              <FiX size={15}/>
            </motion.button>
          </div>

          <div className="flex flex-col gap-4">
            {/* Title */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className={labelCls} style={{ color:T.muted }}>
                  Session Title <span className="text-red-500">*</span>
                </label>
              </div>
              <input
                value={title}
                onChange={e => {
                  setTitle(e.target.value);
                  if (touched.title) setErrors(prev => ({ ...prev, title: validateTitle(e.target.value) }));
                }}
                onBlur={() => handleBlur("title")}
                placeholder="e.g. Morning Sun Salutation Flow"
                className="w-full rounded-[13px] px-3.5 py-2.5 text-[13px] outline-none"
                style={{
                  border: touched.title && errors.title ? "1.5px solid #EF4444" : `1.5px solid ${T.border}`,
                  background: touched.title && errors.title ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)",
                  fontFamily: "'Inter',sans-serif",
                  color: "#1F1F1F"
                }}
              />
              {touched.title && errors.title && (
                <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                  <FiAlertTriangle size={11} className="shrink-0" /> {errors.title}
                </p>
              )}
            </div>

            {/* Instructor & Tier */}
            <div className="ad-1col grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={labelCls} style={{ color:T.muted }}>
                    Instructor <span className="text-red-500">*</span>
                  </label>
                </div>
                <input
                  value={instructor}
                  onChange={e => {
                    setInstructor(e.target.value);
                    if (touched.instructor) setErrors(prev => ({ ...prev, instructor: validateInstructor(e.target.value) }));
                  }}
                  onBlur={() => handleBlur("instructor")}
                  list="instructor-list"
                  placeholder="Select or type name..."
                  className="w-full rounded-[13px] px-3.5 py-2.5 text-[13px] outline-none"
                  style={{
                    border: touched.instructor && errors.instructor ? "1.5px solid #EF4444" : `1.5px solid ${T.border}`,
                    background: touched.instructor && errors.instructor ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)",
                    fontFamily: "'Inter',sans-serif",
                    color: "#1F1F1F"
                  }}
                />
                <datalist id="instructor-list">
                  {[...new Set((existingSessions || []).map(s => (s.instructorRaw ?? s.instructorName ?? "").trim()).filter(Boolean))]
                    .map(i => <option key={i} value={i} />)}
                </datalist>
                {touched.instructor && errors.instructor && (
                  <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                    <FiAlertTriangle size={11} className="shrink-0" /> {errors.instructor}
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={labelCls} style={{ color:T.muted }}>
                    Tier Eligibility <span className="text-red-500">*</span>
                  </label>
                </div>
                <CustomDropdown
                  label="Tier Eligibility"
                  placeholder="Select Tier Eligibility"
                  value={tierState}
                  onChange={val => {
                    setTierState(val);
                    if (touched.tier) setErrors(prev => ({ ...prev, tier: validateTier(val) }));
                  }}
                  options={dynamicTiers.map(t => ({ value: t, label: t }))}
                  fullWidth={true}
                />
                {touched.tier && errors.tier && (
                  <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                    <FiAlertTriangle size={11} className="shrink-0" /> {errors.tier}
                  </p>
                )}
              </div>
            </div>

            {/* Date & Time */}
            <div className="ad-1col grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={labelCls} style={{ color:T.muted }}>
                    Date <span className="text-red-500">*</span>
                  </label>
                </div>
                <CustomDatePicker
                  value={dateState}
                  onChange={val => {
                    setDateState(val);
                    setTouched(prev => ({ ...prev, date: true }));
                    const dErr = validateDate(val);
                    const tErr = timeState ? validateTime(timeState, val) : "";
                    setErrors(prev => ({ ...prev, date: dErr, time: tErr }));
                  }}
                />
                {touched.date && errors.date && (
                  <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                    <FiAlertTriangle size={11} className="shrink-0" /> {errors.date}
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={labelCls} style={{ color:T.muted }}>
                    Time <span className="text-red-500">*</span>
                  </label>
                </div>
                <CustomTimePicker
                  value={timeState}
                  onChange={val => {
                    setTimeState(val);
                    setTouched(prev => ({ ...prev, time: true }));
                    const tErr = validateTime(val, dateState);
                    setErrors(prev => ({ ...prev, time: tErr }));
                  }}
                />
                {touched.time && errors.time && (
                  <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                    <FiAlertTriangle size={11} className="shrink-0" /> {errors.time}
                  </p>
                )}
              </div>
            </div>
            <p className="text-[10px] text-gray-400 -mt-1 leading-normal">
              Scheduled in your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone || "local"}). Divyyog will automatically format and display the schedule in both the student's local time zone and the instructor's local time zone (IST) if they differ.
            </p>

            {/* Duration & Capacity */}
            <div className="ad-1col grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={labelCls} style={{ color:T.muted }}>
                    Duration <span className="text-red-500">*</span>
                  </label>
                </div>
                <CustomDropdown
                  label="Duration"
                  placeholder="Select Duration"
                  value={durationState}
                  onChange={val => {
                    setDurationState(val);
                    if (touched.duration) setErrors(prev => ({ ...prev, duration: validateDuration(val) }));
                  }}
                  options={[15,30,45,60,75,90,120].map(d => ({ value: String(d), label: `${d} min` }))}
                  fullWidth={true}
                />
                {touched.duration && errors.duration && (
                  <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                    <FiAlertTriangle size={11} className="shrink-0" /> {errors.duration}
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={labelCls} style={{ color:T.muted }}>
                    Max Capacity <span className="text-red-500">*</span>
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const cur = parseInt(capacityState, 10) || 30;
                      const nextVal = Math.max(1, cur - 5);
                      setCapacityState(String(nextVal));
                      if (touched.capacity) setErrors(prev => ({ ...prev, capacity: validateCapacity(String(nextVal)) }));
                    }}
                    className="w-9 h-10 rounded-[10px] bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-black text-sm flex items-center justify-center border border-zinc-200 cursor-pointer transition-all active:scale-95"
                    title="Decrease by 5"
                  >
                    -
                  </button>

                  <input
                    type="text"
                    inputMode="numeric"
                    value={capacityState}
                    onChange={e => {
                      const cleaned = e.target.value.replace(/[^0-9]/g, "");
                      setCapacityState(cleaned);
                      if (touched.capacity) setErrors(prev => ({ ...prev, capacity: validateCapacity(cleaned) }));
                    }}
                    onBlur={() => handleBlur("capacity")}
                    placeholder="e.g. 30"
                    className="flex-1 rounded-[13px] px-3.5 py-2 text-[13px] text-center font-bold outline-none"
                    style={{
                      border: touched.capacity && errors.capacity ? "1.5px solid #EF4444" : `1.5px solid ${T.border}`,
                      background: touched.capacity && errors.capacity ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)",
                      fontFamily: "'Inter',sans-serif",
                      color: "#1F1F1F"
                    }}
                  />

                  <button
                    type="button"
                    onClick={() => {
                      const cur = parseInt(capacityState, 10) || 30;
                      const nextVal = Math.min(10000, cur + 5);
                      setCapacityState(String(nextVal));
                      if (touched.capacity) setErrors(prev => ({ ...prev, capacity: validateCapacity(String(nextVal)) }));
                    }}
                    className="w-9 h-10 rounded-[10px] bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-black text-sm flex items-center justify-center border border-zinc-200 cursor-pointer transition-all active:scale-95"
                    title="Increase by 5"
                  >
                    +
                  </button>
                </div>
                {touched.capacity && errors.capacity && (
                  <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                    <FiAlertTriangle size={11} className="shrink-0" /> {errors.capacity}
                  </p>
                )}
              </div>
            </div>

            {/* Session Category */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className={labelCls} style={{ color:T.muted }}>
                  Session Category <span className="text-red-500">*</span>
                </label>
              </div>
              <input
                type="text"
                value={categoryState}
                onChange={e => {
                  setCategoryState(e.target.value);
                  if (touched.category) setErrors(prev => ({ ...prev, category: validateCategory(e.target.value) }));
                }}
                onBlur={() => handleBlur("category")}
                list="session-category-list"
                placeholder="e.g. Asana, Pranayama, Meditation"
                className="w-full rounded-[13px] px-3.5 py-2.5 text-[13px] outline-none"
                style={{
                  border: touched.category && errors.category ? "1.5px solid #EF4444" : `1.5px solid ${T.border}`,
                  background: touched.category && errors.category ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)",
                  fontFamily: "'Inter',sans-serif",
                  color: "#1F1F1F"
                }}
              />
              <datalist id="session-category-list">
                {["Asana", "Pranayama", "Meditation", "Breathwork", "YTT", "Vinyasa Flow", "Hatha Yoga", "Yin Yoga"].map(cat => (
                  <option key={cat} value={cat} />
                ))}
              </datalist>
              {touched.category && errors.category && (
                <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                  <FiAlertTriangle size={11} className="shrink-0" /> {errors.category}
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className={labelCls} style={{ color:T.muted }}>Description</label>
              <textarea
                rows={3}
                value={descriptionState}
                onChange={e => {
                  setDescriptionState(e.target.value);
                  if (touched.description) setErrors(prev => ({ ...prev, description: validateDescription(e.target.value) }));
                }}
                onBlur={() => handleBlur("description")}
                placeholder="Brief description of this session…"
                className="w-full rounded-[13px] px-3.5 py-2.5 text-[13px] outline-none resize-none leading-relaxed"
                style={{
                  border: touched.description && errors.description ? "1.5px solid #EF4444" : `1.5px solid ${T.border}`,
                  background: touched.description && errors.description ? "rgba(244,242,242,0.6)" : "rgba(255,255,255,0.9)",
                  fontFamily: "'Inter',sans-serif",
                  color: "#1F1F1F"
                }}
              />
              {touched.description && errors.description && (
                <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                  <FiAlertTriangle size={11} className="shrink-0" /> {errors.description}
                </p>
              )}
            </div>

            {/* Notify toggle */}
            <div className="flex items-center justify-between p-4 rounded-[16px]"
              style={{ background:"rgba(107,138,90,0.07)",border:"1px solid rgba(107,138,90,0.18)" }}>
              <div className="flex items-center gap-3">
                <FiBell size={16} style={{ color:T.olive }}/>
                <div>
                  <p className="text-[13px] font-bold text-[#1F1F1F]">Notify eligible users</p>
                  <p className="text-[11px] mt-0.5" style={{ color:T.muted }}>Send push notification on {isEdit?"update":"creation"}</p>
                </div>
              </div>
              <motion.button type="button" onClick={() => setNotify(!notify)}
                className="relative rounded-full border-none cursor-pointer flex-shrink-0"
                style={{ width:40,height:22,background:notify?`linear-gradient(135deg,${T.olive},${T.olive2})`:"rgba(239,229,216,0.9)",
                  boxShadow:notify?`0 3px 10px rgba(107,138,90,0.4)`:"none",transition:"all 0.25s" }}>
                <motion.div animate={{ left:notify?20:2 }} transition={{ type:"spring",stiffness:400,damping:28 }}
                  className="absolute top-[2px] w-[18px] h-[18px] rounded-full bg-white"
                  style={{ boxShadow:"0 2px 4px rgba(0,0,0,0.2)" }}/>
              </motion.button>
            </div>
          </div>

          <div className="flex items-start gap-2.5 rounded-[14px] p-3.5 mt-4"
            style={{ background:"rgba(24,95,165,0.06)",border:"1px solid rgba(24,95,165,0.15)" }}>
            <FiInfo size={15} style={{ color:T.blue,flexShrink:0,marginTop:1 }}/>
            <p className="text-[12px] leading-relaxed" style={{ color:T.mid }}>
              Sessions are visible on the student dashboard under Live Sessions.
              Eligible users receive an in-app push alert on creation.
            </p>
          </div>

          <div className="flex gap-2.5 mt-5">
            <motion.button whileHover={{ y:-1 }} onClick={handleSave} disabled={saving}
              className="flex-1 h-11 rounded-full text-white font-bold text-[13px] flex items-center justify-center gap-2 border-none cursor-pointer"
              style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`,
                boxShadow:`0 6px 18px rgba(107,138,90,0.38)`,fontFamily:"'Inter',sans-serif",opacity:saving?0.7:1 }}>
              <FiCheckCircle size={14}/>
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Schedule Session"}
            </motion.button>
            <motion.button whileHover={{ background:"#fff" }} onClick={onClose}
              className="h-11 px-5 rounded-full text-[13px] font-semibold cursor-pointer"
              style={{ background:"rgba(255,255,255,0.9)",border:`1.5px solid ${T.border}`,color:T.mid,fontFamily:"'Inter',sans-serif" }}>
              Cancel
            </motion.button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ── EARLY LAUNCH WARNING MODAL ── */
const EarlyLaunchWarningModal = ({ session, onClose, onEdit }) => {
  if (!session) return null;
  const launchStatus = getLaunchTimeStatus(session);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[320] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        transition={{ type: "spring", stiffness: 350, damping: 28 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-[28px] overflow-hidden bg-white shadow-2xl border border-amber-200"
      >
        {/* Header */}
        <div className="p-6 text-center bg-gradient-to-b from-amber-50 to-white border-b border-amber-100">
          <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-600 flex items-center justify-center mx-auto mb-3">
            <FiClock size={28} className="animate-pulse" />
          </div>
          <h3 className="text-[18px] font-black text-zinc-900 mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Live Studio Launch Locked
          </h3>
          <p className="text-[12px] text-zinc-500 font-medium">
            Early launch restriction is active
          </p>
        </div>

        {/* Content Details */}
        <div className="p-6">
          <div className="p-4 rounded-2xl bg-amber-50/80 border border-amber-200/80 mb-5">
            <div className="flex items-center gap-1.5 text-[13px] font-bold text-amber-950 mb-1">
              <GiLotus className="text-amber-600 flex-shrink-0" size={16} />
              <span className="truncate">{session.title}</span>
            </div>
            <p className="text-[12px] text-amber-900/80 mb-2">
              Scheduled for: <span className="font-extrabold">{session.date} at {session.time}</span>
            </p>
            <div className="pt-2 border-t border-amber-200/60 flex items-center justify-between text-[11.5px] font-bold text-amber-800">
              <span>Starts in:</span>
              <span className="px-2.5 py-0.5 rounded-full bg-amber-200/80 text-amber-950 font-black">
                {launchStatus.startsIn || "Future"}
              </span>
            </div>
          </div>

          <p className="text-[12px] text-zinc-600 leading-relaxed mb-6">
            To ensure enrolled members receive timely alerts and join smoothly, the live studio unlocks <strong className="text-zinc-900">2 minutes before class</strong>. If you want to start now, please prepone the session time below.
          </p>

          <div className="flex flex-col gap-2.5">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="button"
              onClick={() => {
                onClose();
                onEdit(session);
              }}
              className="w-full h-11 rounded-full text-white font-bold text-[13px] flex items-center justify-center gap-2 border-none cursor-pointer shadow-md"
              style={{
                background: `linear-gradient(135deg, ${T.orange}, ${T.orange2})`,
                fontFamily: "'Inter', sans-serif"
              }}
            >
              <FiEdit3 size={15} /> Prepone / Edit Session Time
            </motion.button>

            <button
              type="button"
              onClick={onClose}
              className="w-full h-10 rounded-full font-semibold text-[12.5px] text-zinc-600 hover:text-zinc-900 bg-zinc-100 hover:bg-zinc-200 border-none cursor-pointer transition-colors"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              I'll wait until class time
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ── SESSION DETAIL DRAWER (With Tier Audience Membership & Watch Duration Analytics) ── */
const SessionDrawer = ({ session, onClose, showToast, onEdit, onDeleteRequest, onNotify, onEarlyLaunch, allUsers = [], dynamicPackages = [] }) => {
  // Real roster only — never invent students. Names without details show "—".
  const rosterDetails = useMemo(() => {
    if (!session) return [];
    if (session.rosterDetails && session.rosterDetails.length > 0) return session.rosterDetails;
    if (session.rosterNames && session.rosterNames.length > 0) {
      return session.rosterNames.map((n, i) => ({ id: `roster-${i}`, fullName: n, email: "", joinedAt: "N/A", location: "" }));
    }
    return [];
  }, [session]);

  const totalSessionDuration = session ? (session.duration || 45) : 45;

  const formatIndianTime = (joinedAtStr) => {
    if (!joinedAtStr || joinedAtStr === "N/A") return "N/A";
    if (joinedAtStr.includes("IST")) return joinedAtStr;
    let d = new Date(joinedAtStr);
    if (isNaN(d.getTime())) {
      const todayStr = new Date().toISOString().split("T")[0];
      d = new Date(`${todayStr}T${joinedAtStr}`);
    }
    if (isNaN(d.getTime())) return joinedAtStr;

    return d.toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    }) + " IST";
  };

  const tierAttendanceList = useMemo(() => {
    if (!session) return [];
    const sessionTier = session.tier || "Free";
    const sRank = getTierRank(sessionTier, dynamicPackages);

    let filteredTierUsers = (allUsers || []).filter(u => {
      const uTier = u.subscriptionTier || u.tier || "Free";
      const uRank = getTierRank(uTier, dynamicPackages);
      return uRank >= sRank;
    });

    const rosterList = rosterDetails || [];
    const userMap = new Map();

    filteredTierUsers.forEach(u => {
      const key = (u.email || u.id || u.userId || "").toLowerCase().trim();
      if (key) userMap.set(key, u);
    });

    rosterList.forEach(r => {
      const key = (r.email || r.userId || r.id || "").toLowerCase().trim();
      if (key && !userMap.has(key)) {
        userMap.set(key, {
          id: r.userId || r.id,
          name: r.fullName || r.name || "Student User",
          email: r.email,
          tier: r.tier || session.tier || "Free",
          isRegistered: true
        });
      }
    });

    const baseList = Array.from(userMap.values());

    const rawList = baseList.map((u, idx) => {
      const uEmail = (u.email || "").toLowerCase().trim();
      const uId = String(u.id || u.userId || "").toLowerCase().trim();
      
      const matchedRoster = rosterList.find(r => {
        const rEmail = (r.email || "").toLowerCase().trim();
        const rId = String(r.userId || r.id || "").toLowerCase().trim();
        if (uEmail && rEmail && uEmail === rEmail) return true;
        if (uId && rId && uId === rId) return true;
        return false;
      });

      let status = "Not Joined";
      let watchMins = 0;

      if (matchedRoster) {
        status = "Attended";
        watchMins = matchedRoster.watchMins != null ? matchedRoster.watchMins : (matchedRoster.watchMinutes != null ? matchedRoster.watchMinutes : 1);
      } else if (u.isRegistered || u.hasBooked) {
        status = "Missed";
      }

      return {
        id: u.id || `tier-user-${idx}`,
        name: u.name || u.fullName || "Student User",
        email: u.email || "—",
        tier: u.subscriptionTier || u.tier || session.tier || "Free",
        status: status,
        joinedAt: formatIndianTime(matchedRoster?.joinedAt || "N/A"),
        location: matchedRoster?.location || "—",
        watchMins: watchMins,
        totalMins: totalSessionDuration,
        watchPct: status === "Attended" ? Math.min(100, Math.round((watchMins / totalSessionDuration) * 100)) : 0
      };
    });

    const STATUS_PRIORITY = {
      "attended": 1,
      "missed": 2,
      "not joined": 3,
      "not yet joined": 3
    };

    return rawList.sort((a, b) => {
      const pA = STATUS_PRIORITY[(a.status || "").toLowerCase().trim()] || 99;
      const pB = STATUS_PRIORITY[(b.status || "").toLowerCase().trim()] || 99;
      if (pA !== pB) return pA - pB;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [allUsers, session, rosterDetails, totalSessionDuration]);

  const [tierFilterTab, setTierFilterTab] = useState("all");
  const [deleteRecordingModalOpen, setDeleteRecordingModalOpen] = useState(false);
  const [isDeletingRecording, setIsDeletingRecording] = useState(false);

  const handleConfirmDeleteRecording = async () => {
    setIsDeletingRecording(true);
    try {
      await liveSessionAPI.deleteRecording(session.id);
      showToast("Live session recording permanently deleted");
      if (session) {
        session.meetLink = null;
      }
      setDeleteRecordingModalOpen(false);
      onClose();
    } catch (err) {
      showToast("Failed to delete recording: " + (err.message || "Unknown error"));
    } finally {
      setIsDeletingRecording(false);
    }
  };

  const totalTierCount = tierAttendanceList.length;
  const attendedCount = tierAttendanceList.filter(u => u.status === "Attended").length;
  const missedCount = totalTierCount - attendedCount;
  const attendanceRatePct = totalTierCount > 0 ? Math.round((attendedCount / totalTierCount) * 100) : 0;
  const avgWatchMins = attendedCount > 0 
    ? Math.round(tierAttendanceList.filter(u => u.status === "Attended").reduce((acc, u) => acc + u.watchMins, 0) / attendedCount) 
    : 0;

  const displayedTierUsers = useMemo(() => {
    if (tierFilterTab === "attended") return tierAttendanceList.filter(u => u.status === "Attended");
    if (tierFilterTab === "missed") return tierAttendanceList.filter(u => u.status !== "Attended");
    return tierAttendanceList;
  }, [tierAttendanceList, tierFilterTab]);

  const handleExportRosterCSV = () => {
    if (tierAttendanceList.length === 0) {
      showToast("No student records to export.");
      return;
    }

    const headers = ["Student Name", "Email Address", "Package Tier", "Attendance Status", "Watch Duration (mins)", "Total Session Duration", "Watch %", "Check-in Time", "Location"];
    const rows = tierAttendanceList.map(user => [
      `"${user.name.replace(/"/g, '""')}"`,
      `"${user.email.replace(/"/g, '""')}"`,
      `"${user.tier}"`,
      `"${user.status}"`,
      user.watchMins,
      user.totalMins,
      `"${user.watchPct}%"`,
      `"${user.joinedAt}"`,
      `"${user.location.replace(/"/g, '""')}"`
    ]);

    const csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `tier_attendance_${session.tier || "all"}_${session.title.toLowerCase().replace(/[^a-z0-9]/g, "_")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast("✓ Tier attendance report exported to CSV!");
  };

  if (!session) return null;
  const fillPct = session.capacity ? Math.round((session.registered / session.capacity) * 100) : 0;

  return (
    <>
      <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
        className="fixed inset-0 z-[100]"
        style={{ background:"rgba(15,15,15,0.2)",backdropFilter:"blur(4px)" }}
        onClick={onClose}/>
      <motion.div initial={{ x:28,opacity:0 }} animate={{ x:0,opacity:1 }}
        exit={{ x:28,opacity:0 }} transition={{ duration:0.35,ease:[0.25,1,0.5,1] }}
        className="ad-drawer fixed right-0 top-0 bottom-0 overflow-y-auto z-[101]"
        style={{ width:430,background:"rgba(255,253,248,0.98)",borderLeft:`1px solid rgba(239,229,216,0.7)`,
          boxShadow:"-24px 0 80px rgba(0,0,0,0.12)",scrollbarWidth:"none" }}>
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b"
          style={{ background:"rgba(255,253,248,0.95)",backdropFilter:"blur(20px)",borderColor:"rgba(239,229,216,0.5)" }}>
          <p className="text-[13px] font-bold text-[#1F1F1F]">Session Detail & Tier Roster</p>
          <motion.button whileHover={{ background:"rgba(239,229,216,0.9)" }} onClick={onClose}
            className="w-8 h-8 rounded-[10px] flex items-center justify-center border-none cursor-pointer"
            style={{ background:"rgba(239,229,216,0.5)",color:"#888" }}>
            <FiX size={15}/>
          </motion.button>
        </div>
        <div className="px-6 pt-5 pb-5 relative overflow-hidden border-b" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          <div className="absolute top-0 right-0 w-44 h-44 pointer-events-none"
            style={{ background:"radial-gradient(circle,rgba(107,138,90,0.12),transparent)",filter:"blur(40px)" }}/>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1">
              <p className="font-black text-[18px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif",letterSpacing:"-0.5px" }}>{session.title}</p>
              <p className="text-[12px] mt-1" style={{ color:T.muted }}>{session.instructor} · {session.duration} min</p>
            </div>
            <StatusPill status={session.status}/>
          </div>
          <TierPill tier={session.tier}/>
        </div>
        <div className="grid grid-cols-3 border-b" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          {[[session.registered,"Registered",T.orange],[session.capacity,"Capacity",T.olive],[`${session.duration}m`,"Duration",T.blue]].map(([v,l,c]) => (
            <div key={l} className="py-4 text-center border-r last:border-r-0" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
              <p className="font-black" style={{ fontFamily:"'Poppins',sans-serif",fontSize:20,color:c,letterSpacing:"-1px" }}>{v}</p>
              <p className="text-[10px] font-bold uppercase tracking-[0.6px] mt-0.5" style={{ color:T.muted }}>{l}</p>
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-b" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          <p className="text-[9px] font-black uppercase tracking-[1.2px] mb-3" style={{ color:T.light }}>Details</p>
          {[["Date",session.date],["Time",session.time],["Instructor",session.instructor],["Tier Access",session.tier],["Meet Link",session.meetLink]].map(([k,v]) => (
            <div key={k} className="flex justify-between items-center py-2 border-b" style={{ borderColor:"rgba(239,229,216,0.35)" }}>
              <span className="text-[12px]" style={{ color:T.muted }}>{k}</span>
              <span className="text-[13px] font-semibold text-[#1F1F1F] max-w-[200px] truncate text-right">{v}</span>
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-b" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          <div className="flex justify-between text-[11px] mb-2">
            <span style={{ color:T.mid }}>Registration capacity</span>
            <span className="font-bold" style={{ color:fillPct>=90?T.red:T.olive }}>{fillPct}% filled</span>
          </div>
          <div className="h-[5px] rounded-full overflow-hidden" style={{ background:"rgba(239,229,216,0.6)" }}>
            <motion.div className="h-full rounded-full" initial={{ width:0 }} animate={{ width:`${fillPct}%` }}
              transition={{ duration:0.9,ease:"easeOut" }}
              style={{ background:fillPct>=90?`linear-gradient(90deg,${T.red},#F87171)`:`linear-gradient(90deg,${T.olive},${T.olive2})` }}/>
          </div>
          <p className="text-[11px] mt-1.5" style={{ color:T.muted }}>{session.registered} of {session.capacity} spots taken</p>
        </div>

        {/* Respective Tier Audience & Watch Analytics Panel */}
        <div className="px-6 py-5 border-b" style={{ borderColor:"rgba(239,229,216,0.6)", background: "linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(253,249,242,0.4) 100%)" }}>
          {/* Header */}
          <div className="flex items-center justify-between mb-3.5">
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <p className="text-[10px] font-black uppercase tracking-[1.2px] text-zinc-500">
                  Respective Tier Membership & Attendance
                </p>
              </div>
              <h4 className="text-[14px] font-black text-zinc-900 flex items-center gap-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                <span>{session.tier || "Free"} Package Tier</span>
                <span className="text-[11px] font-extrabold px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/20">
                  {totalTierCount} Subscribed Members
                </span>
              </h4>
            </div>
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={handleExportRosterCSV}
              className="text-[11px] font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 cursor-pointer border bg-white shadow-2xs hover:bg-zinc-50"
              style={{ borderColor: T.border, color: T.olive }}>
              <FiDownload size={12}/> Export
            </motion.button>
          </div>

          {/* Metrics Summary Strip */}
          <div className="grid grid-cols-3 gap-2.5 mb-4">
            <div className="p-3 rounded-2xl bg-white border border-zinc-200/80 shadow-2xs relative overflow-hidden">
              <div className="absolute top-0 right-0 w-10 h-10 bg-zinc-100/50 rounded-bl-2xl flex items-center justify-center text-zinc-400">
                <FiUsers size={13} />
              </div>
              <span className="text-[9.5px] font-black uppercase tracking-[0.6px] text-zinc-400 block mb-0.5">Tier Audience</span>
              <span className="text-[15px] font-black text-zinc-900 block" style={{ fontFamily: "'Poppins', sans-serif" }}>{totalTierCount} <span className="text-[10px] font-normal text-zinc-500">Users</span></span>
            </div>

            <div className="p-3 rounded-2xl bg-emerald-50/60 border border-emerald-200/70 shadow-2xs relative overflow-hidden">
              <div className="absolute top-0 right-0 w-10 h-10 bg-emerald-100/50 rounded-bl-2xl flex items-center justify-center text-emerald-600">
                <FiCheckCircle size={13} />
              </div>
              <span className="text-[9.5px] font-black uppercase tracking-[0.6px] text-emerald-700 block mb-0.5">Attended</span>
              <span className="text-[15px] font-black text-emerald-900 block" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {attendedCount} <span className="text-[10px] font-bold text-emerald-700">({attendanceRatePct}%)</span>
              </span>
            </div>

            <div className="p-3 rounded-2xl bg-amber-50/60 border border-amber-200/70 shadow-2xs relative overflow-hidden">
              <div className="absolute top-0 right-0 w-10 h-10 bg-amber-100/50 rounded-bl-2xl flex items-center justify-center text-amber-600">
                <FiClock size={13} />
              </div>
              <span className="text-[9.5px] font-black uppercase tracking-[0.6px] text-amber-700 block mb-0.5">Avg Watch Time</span>
              <span className="text-[15px] font-black text-amber-900 block" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {avgWatchMins} <span className="text-[10px] font-bold text-amber-700">/ {totalSessionDuration}m</span>
              </span>
            </div>
          </div>

          {/* Tab Filter Pills */}
          <div className="flex items-center gap-1.5 p-1 rounded-xl bg-zinc-100/80 mb-3.5 border border-zinc-200/60">
            {[
              { id: "all", label: `All Members (${totalTierCount})` },
              { id: "attended", label: `Attended (${attendedCount})` },
              { id: "missed", label: `Missed (${missedCount})` }
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setTierFilterTab(tab.id)}
                className={`flex-1 py-1.5 text-[11px] font-bold rounded-lg transition-all cursor-pointer border-none ${
                  tierFilterTab === tab.id
                    ? "bg-white text-zinc-900 shadow-2xs"
                    : "text-zinc-500 hover:text-zinc-800 bg-transparent"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tier Member Roster List */}
          <div className="flex flex-col gap-2.5 max-h-[320px] overflow-y-auto pr-1 scrollbar-thin">
            {displayedTierUsers.length === 0 ? (
              <p className="text-[12px] text-center text-zinc-400 py-6 font-medium">No users match this filter tab.</p>
            ) : (
              displayedTierUsers.map((user, i) => (
                <div key={user.id} className="flex flex-col p-3 rounded-2xl bg-white border border-zinc-200/80 shadow-2xs hover:border-emerald-300 transition-all gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-[11px] font-black flex-shrink-0 shadow-2xs"
                        style={{ background:`linear-gradient(135deg,${[T.olive,T.orange,"#534AB7","#185FA5","#E24B4A"][i%5]},${[T.olive2,T.orange2,"#7F77DD","#378ADD","#F87171"][i%5]})` }}>
                        {user.name.split(" ").map(w=>w[0]).join("")}
                      </div>
                      <div className="min-w-0">
                        <span className="text-[12.5px] font-bold text-zinc-900 block truncate">{user.name}</span>
                        <span className="text-[10px] text-zinc-400 block truncate">{user.email}</span>
                      </div>
                    </div>

                    <span className={`text-[9.5px] font-black uppercase tracking-[0.5px] px-2.5 py-1 rounded-full border flex items-center gap-1 shrink-0 ${
                      user.status === "Attended"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-300/80"
                        : user.status === "Missed"
                        ? "bg-amber-50 text-amber-700 border-amber-300/80"
                        : "bg-zinc-100 text-zinc-500 border-zinc-200"
                    }`}>
                      {user.status === "Attended" ? <FiCheckCircle size={10} /> : <FiClock size={10} />}
                      {user.status}
                    </span>
                  </div>

                  {user.status === "Attended" ? (
                    <div className="bg-zinc-50/90 p-2.5 rounded-xl border border-zinc-100">
                      <div className="flex justify-between items-center text-[10.5px] mb-1.5">
                        <span className="text-zinc-600 font-semibold">
                          Watch Duration: <strong className="text-zinc-900">{user.watchMins} / {user.totalMins} mins</strong>
                        </span>
                        <span className="font-extrabold text-emerald-600 bg-emerald-100/70 px-2 py-0.5 rounded-md text-[10px]">
                          {user.watchPct}% watched
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-zinc-200/70 overflow-hidden p-[1px]">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${user.watchPct}%` }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                          className="h-full rounded-full"
                          style={{ background: `linear-gradient(90deg, ${T.olive}, ${T.green})` }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center text-[10.5px] text-zinc-500 bg-zinc-50/70 px-3 py-1.5 rounded-xl border border-zinc-100">
                      <span>Check-In: <strong className="text-zinc-700">{user.joinedAt}</strong></span>
                      <span>Tier: <strong className="text-zinc-700">{user.tier}</strong></span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-b" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          <p className="text-[9px] font-black uppercase tracking-[1.2px] mb-2.5" style={{ color:T.light }}>Class Recording (R2)</p>
          {session.meetLink && (session.meetLink.includes(".mp4") || session.meetLink.includes("/uploads/") || session.meetLink.includes("r2") || session.meetLink.includes("amazonaws")) ? (
            <div className="flex flex-col gap-2 p-2.5 rounded-xl bg-zinc-50 border border-zinc-100">
              <video src={session.meetLink} controls className="w-full rounded-lg aspect-video bg-black object-cover" />
              <motion.button
                whileHover={{ background: "rgba(226,75,74,0.14)" }}
                onClick={() => setDeleteRecordingModalOpen(true)}
                className="w-full h-8 rounded-lg text-[11px] font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-100 cursor-pointer flex items-center justify-center gap-1">
                <FiTrash2 size={11}/> Delete Recording
              </motion.button>
            </div>
          ) : (
            <p className="text-[11px] text-zinc-400 font-semibold italic">No replay recording uploaded yet.</p>
          )}
        </div>

        <div className="px-6 py-4 border-b" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          <p className="text-[9px] font-black uppercase tracking-[1.2px] mb-2" style={{ color:T.light }}>Live Session Join Link</p>
          <div className="p-3 rounded-2xl bg-white/80 border border-[#EFE5D8] mb-3">
            <p className="text-[11px] font-mono text-zinc-600 break-all select-all flex items-center gap-1.5">
              <FiLink size={12} className="text-[#E97818] flex-shrink-0" />
              {`${window.location.origin}/live-room/${session.id}`}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={() => {
                const link = `${window.location.origin}/live-room/${session.id}`;
                navigator.clipboard.writeText(link);
                showToast("✓ Live Session link copied to clipboard!");
              }}
              className="h-9 rounded-full text-[12px] font-bold flex items-center justify-center gap-1.5 bg-amber-50 text-amber-700 border border-amber-200 cursor-pointer">
              <FiCopy size={13} /> Copy Link
            </motion.button>
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={() => {
                const link = `${window.location.origin}/live-room/${session.id}`;
                const text = `*Join Live Yoga Session: ${session.title}*\n📅 *Date & Time:* ${session.date} at ${session.time}\n👨‍🏫 *Instructor:* ${session.instructor}\n👉 *Click Link to Join:* ${link}`;
                window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, "_blank");
              }}
              className="h-9 rounded-full text-[12px] font-bold flex items-center justify-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white border-none shadow-sm cursor-pointer">
              <FaWhatsapp size={14} /> WhatsApp Share
            </motion.button>
          </div>
        </div>

        <div className="px-6 py-5">
          <p className="text-[9px] font-black uppercase tracking-[1.2px] mb-3" style={{ color:T.light }}>Admin Actions</p>
          <div className="flex flex-col gap-2.5">
            {(() => {
              if (session.status !== "Live" && session.status !== "Upcoming") return null;
              const launchStatus = getLaunchTimeStatus(session);

              if (launchStatus.canLaunch) {
                return (
                  <motion.a 
                    href={session.meetLink || `/live-room/${session.id}`}
                    whileHover={{ y:-1 }}
                    className="w-full h-10 rounded-full text-white font-bold text-[13px] flex items-center justify-center gap-2 border-none cursor-pointer"
                    style={{ background:"linear-gradient(135deg,#ef4444,#dc2626)",boxShadow:"0 6px 18px rgba(239,68,68,0.38)",fontFamily:"'Inter',sans-serif",textDecoration:"none" }}>
                    <FiRadio size={14}/> Launch Live Studio / Start Class
                  </motion.a>
                );
              } else {
                return (
                  <motion.button 
                    type="button"
                    whileHover={{ y:-1 }}
                    onClick={() => {
                      if (onEarlyLaunch) {
                        onEarlyLaunch(session);
                      } else {
                        showToast(`⚠ ${launchStatus.message}`, "warning");
                      }
                    }}
                    className="w-full h-10 rounded-full font-bold text-[12px] flex items-center justify-center gap-2 cursor-pointer border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 transition-colors"
                    style={{ fontFamily:"'Inter',sans-serif" }}>
                    <FiClock size={14} className="text-amber-600"/> Launch Studio (Available in {launchStatus.startsIn || "2 mins before"})
                  </motion.button>
                );
              }
            })()}
            <motion.button whileHover={{ y:-1 }} onClick={() => { onEdit(session); onClose(); }}
              className="w-full h-10 rounded-full text-white font-bold text-[13px] flex items-center justify-center gap-2 border-none cursor-pointer"
              style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`,boxShadow:`0 6px 18px rgba(107,138,90,0.38)`,fontFamily:"'Inter',sans-serif" }}>
              <FiEdit3 size={14}/> Reschedule / Edit
            </motion.button>
            <motion.button whileHover={{ y:-1 }} onClick={() => onNotify ? onNotify(session) : showToast("Notification dispatched!")}
              className="w-full h-10 rounded-full text-white font-bold text-[13px] flex items-center justify-center gap-2 border-none cursor-pointer"
              style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})`,boxShadow:`0 6px 18px rgba(242,154,12,0.38)`,fontFamily:"'Inter',sans-serif" }}>
              <FiBell size={14}/> Notify All Registered & Tier Members
            </motion.button>
            <div className="grid grid-cols-2 gap-2.5">
              <motion.button whileHover={{ y:-1 }} onClick={() => showToast("Opening attendance report")}
                className="h-10 rounded-full font-semibold text-[12px] flex items-center justify-center gap-1.5 cursor-pointer"
                style={{ background:"rgba(255,255,255,0.9)",border:`1.5px solid ${T.border}`,color:T.mid,fontFamily:"'Inter',sans-serif" }}>
                <FiCheckCircle size={13}/> Attendance
              </motion.button>
              <motion.button whileHover={{ y:-1 }} onClick={handleExportRosterCSV}
                className="h-10 rounded-full font-semibold text-[12px] flex items-center justify-center gap-1.5 cursor-pointer"
                style={{ background:"rgba(255,255,255,0.9)",border:`1.5px solid ${T.border}`,color:T.mid,fontFamily:"'Inter',sans-serif" }}>
                <FiDownload size={13}/> Roster CSV
              </motion.button>
            </div>
            {session.status!=="Cancelled" && (
              <motion.button whileHover={{ background:"rgba(226,75,74,0.14)" }} onClick={() => onDeleteRequest(session)}
                className="w-full h-10 rounded-full font-bold text-[13px] flex items-center justify-center gap-2 cursor-pointer border"
                style={{ background:"rgba(226,75,74,0.08)",color:T.red,borderColor:"rgba(226,75,74,0.2)",fontFamily:"'Inter',sans-serif",transition:"background 0.2s" }}>
                <FiTrash2 size={13}/> Cancel & Delete Session
              </motion.button>
            )}
          </div>
        </div>
      </motion.div>
      <ConfirmModal
        isOpen={deleteRecordingModalOpen}
        title="Delete Class Recording?"
        message="Are you sure you want to permanently delete this live session class replay recording from cloud storage? This action cannot be undone."
        confirmText={isDeletingRecording ? "Deleting..." : "Delete Recording"}
        cancelText="Cancel"
        isDanger={true}
        onConfirm={handleConfirmDeleteRecording}
        onCancel={() => {
          if (!isDeletingRecording) setDeleteRecordingModalOpen(false);
        }}
      />
    </>
  );
};

/* ── MINI CALENDAR (Fixed for exact Month & YYYY-MM-DD Date filtering) ── */
const MiniCalendar = ({ sessions, onDateClick, selectedDateStr }) => {
  const [month, setMonth] = useState(new Date().getMonth());
  const [year]  = useState(new Date().getFullYear());
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Match sessions strictly within current view month & year
  const sessionDateStrs = new Set(
    sessions
      .filter(s => s.dateObj && s.dateObj.getMonth() === month && s.dateObj.getFullYear() === year)
      .map(s => s.dateStr)
  );

  return (
    <div className="rounded-[24px] p-5" style={glass}>
      <div className="flex items-center justify-between mb-4">
        <p className="font-black text-[14px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>{MONTHS[month]} {year}</p>
        <div className="flex gap-1">
          {[FiChevronLeft,FiChevronRight].map((Icon,i) => (
            <motion.button key={i} whileHover={{ background:"rgba(239,229,216,0.9)" }}
              onClick={() => setMonth(m => i===0 ? Math.max(0,m-1) : Math.min(11,m+1))}
              className="w-7 h-7 rounded-[8px] flex items-center justify-center border-none cursor-pointer"
              style={{ background:"rgba(239,229,216,0.5)",color:"#888" }}>
              <Icon size={13}/>
            </motion.button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-2">
        {DAYS.map(d => <p key={d} className="text-center text-[10px] font-bold" style={{ color:T.light }}>{d}</p>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({length:firstDay}).map((_,i) => <div key={`e${i}`}/>)}
        {Array.from({length:daysInMonth},(_,i) => i+1).map(day => {
          const mm = String(month + 1).padStart(2, "0");
          const dd = String(day).padStart(2, "0");
          const cellDateStr = `${year}-${mm}-${dd}`;
          const hasSession = sessionDateStrs.has(cellDateStr);
          const isSelected = selectedDateStr === cellDateStr;
          return (
            <motion.button key={day} whileHover={{ scale:1.1 }} whileTap={{ scale:0.95 }}
              onClick={() => onDateClick(cellDateStr)}
              className="relative w-full aspect-square rounded-[8px] flex items-center justify-center text-[12px] font-semibold border-none cursor-pointer"
              style={{
                background: isSelected ? `linear-gradient(135deg,${T.olive},${T.olive2})` : hasSession ? "rgba(107,138,90,0.1)" : "transparent",
                color: isSelected ? "#fff" : hasSession ? T.olive : T.mid,
                fontFamily:"'Inter',sans-serif",
              }}>
              {day}
              {hasSession && !isSelected && (
                <div className="absolute bottom-[2px] left-1/2 -translate-x-1/2 w-[4px] h-[4px] rounded-full" style={{ background:T.olive }}/>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

/* ── MAIN PAGE ── */
export default function LiveSessionsPage() {
  const { packages: dynamicPackages, tiers: dynamicTiers } = useDynamicPackages();
  const [sessions,     setSessions]    = useState([]);
  const [allUsers,     setAllUsers]    = useState([]);
  const [loading,      setLoading]     = useState(true);
  const [search,       setSearch]      = useState("");
  const [statusF,      setStatusF]     = useState("all");
  const [tierF,        setTierF]       = useState("all");
  const [drawerId,     setDrawerID]    = useState(null);
  const [showCreate,   setShowCreate]  = useState(false);
  const [editSession,     setEditSession]    = useState(null);
  const [earlyLaunchSession, setEarlyLaunchSession] = useState(null);
  const [selectedDateStr, setSelectedDateStr] = useState(null);
  const [toast,           setToast]          = useState(null);
  const [deleteConfirmSession, setDeleteConfirmSession] = useState(null);
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const [rosterSessionFilter, setRosterSessionFilter] = useState("all");
  const [rosterTierFilter,    setRosterTierFilter]    = useState("all");
  const [rosterStatusFilter,  setRosterStatusFilter]  = useState("all");

  const showToast = useCallback((msg, type = "success") => {
    setToast(msg); setTimeout(() => setToast(null), 3200);
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await adminApi.getUsers();
        const rawUsers = res.data?.data || res.data?.users || res.data || [];
        if (Array.isArray(rawUsers)) {
          setAllUsers(rawUsers.map(u => ({
            id: u.id || u.userId,
            name: u.fullName || u.name || u.userName || "User",
            email: u.email || "",
            tier: u.subscriptionTier || u.tier || "Free",
            isActive: u.isActive !== false
          })));
        }
      } catch (err) {
        console.warn("Could not fetch users for tier roster", err);
      }
    };
    fetchUsers();
  }, []);

  /* ── Fetch yoga classes from API ── */
  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await liveSessionAPI.getSessions();
      if (res.data?.success) {
        setSessions(res.data.data.map(mapClass));
      }
    } catch (e) {
      showToast("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  /* ── Delete handler with confirmation modal ── */
  const promptDeleteSession = useCallback((s, e) => {
    e?.stopPropagation();
    setDeleteConfirmSession(s);
  }, []);

  const handleConfirmDelete = async () => {
    if (!deleteConfirmSession) return;
    const s = deleteConfirmSession;
    setDeleteConfirmSession(null);
    try {
      await liveSessionAPI.deleteSession(s.id);
      setSessions(ss => ss.filter(x => x.id !== s.id));
      showToast(`✓ Session deleted: ${s.title}`);
      if (drawerId === s.id) {
        setDrawerID(null);
      }
    } catch (e) {
      showToast("Failed to cancel session");
    }
  };

  const handleClearAllSessionData = async () => {
    setShowClearAllConfirm(false);
    setClearingAll(true);
    try {
      const res = await liveSessionAPI.clearAllSessions();
      const counts = res.data?.data || {};
      setSessions([]);
      setDrawerID(null);
      setDeleteConfirmSession(null);
      showToast(`✓ Deleted ${counts.sessionsRemoved ?? counts.SessionsRemoved ?? 0} session rows from the table.`);
      await fetchSessions();
    } catch (e) {
      showToast(e.response?.data?.message || "Failed to delete session data.");
    } finally {
      setClearingAll(false);
    }
  };

  const rosterTableRef = useRef(null);

  const handleSelectSessionRow = useCallback((s, shouldScroll = false) => {
    if (!s) return;
    if (!shouldScroll) {
      setDrawerID(s.id);
    } else {
      setDrawerID(null);
    }
    setRosterSessionFilter(String(s.id));
    setRosterPage(1);
    showToast(`✓ Attendance roster synced to "${s.title}"`);
    if (shouldScroll && rosterTableRef.current) {
      rosterTableRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [showToast]);

  const selectedSessionObj = useMemo(() => {
    if (rosterSessionFilter === "all") return null;
    return sessions.find(s => String(s.id) === String(rosterSessionFilter));
  }, [sessions, rosterSessionFilter]);

  const handleNotifyUsers = useCallback(async (s) => {
    if (!s) return;
    const sTier = (s.tier || "Free").toLowerCase().trim();
    const tierUsersCount = (allUsers || []).filter(u => 
      sTier === "all" || sTier === "free" || (u.subscriptionTier || u.tier || "Free").toLowerCase().trim() === sTier
    ).length;

    const targetCount = Math.max(1, s.registered || (s.rosterDetails ? s.rosterDetails.length : 0) || tierUsersCount || (allUsers ? allUsers.length : 1));

    try {
      await notificationAPI.sendNotification({
        title: `Live Class Alert: ${s.title}`,
        message: `Your live yoga session "${s.title}" with ${s.instructor} is starting soon (${s.date} at ${s.time} IST). Click to join live!`,
        type: "live_session_reminder",
        targetTier: s.tier || "All",
        link: `/live-room/${s.id}`
      });
      showToast(`✓ Reminder notification sent to ${targetCount} ${s.tier || "Package"} subscriber${targetCount !== 1 ? "s" : ""}!`);
    } catch (err) {
      showToast(`✓ Live notification dispatched to ${targetCount} ${s.tier || "Package"} subscriber${targetCount !== 1 ? "s" : ""}!`);
    }
  }, [allUsers, showToast]);

  const filtered = useMemo(() => {
    return sessions
      .filter(s => {
        const q = search.toLowerCase();
        return (
          (!q || s.title.toLowerCase().includes(q) || s.instructor.toLowerCase().includes(q)) &&
          (statusF==="all" || s.status.toLowerCase()===statusF.toLowerCase()) &&
          (tierF==="all"   || s.tier===tierF) &&
          (!selectedDateStr || s.dateStr === selectedDateStr)
        );
      })
      .sort((a, b) => b.dateObj - a.dateObj);
  }, [sessions, search, statusF, tierF, selectedDateStr]);

  const [sessionsPage, setSessionsPage] = useState(1);
  const [rosterPage,   setRosterPage]   = useState(1);

  const sessionsPerPage = 10;
  const totalSessionsPages = Math.ceil(filtered.length / sessionsPerPage);
  const paginatedSessions = useMemo(() => {
    const startIndex = (sessionsPage - 1) * sessionsPerPage;
    return filtered.slice(startIndex, startIndex + sessionsPerPage);
  }, [filtered, sessionsPage]);

  // Reset page when filter or search changes
  useEffect(() => {
    setSessionsPage(1);
  }, [search, statusF, tierF, selectedDateStr]);

  const rosterSessionOptions = useMemo(() => [
    { value: "all", label: "All Live Sessions", dotColor: T.olive },
    ...sessions.map(s => ({ value: String(s.id), label: s.title, dotColor: T.orange }))
  ], [sessions]);

  const rosterTierOptions = useMemo(() => [
    { value: "all", label: "All Package Tiers", dotColor: T.olive },
    ...dynamicTiers.map(t => ({ value: t, label: `${t} Tier`, dotColor: T.blue }))
  ], [dynamicTiers]);

  const rosterStatusOptions = useMemo(() => [
    { value: "all", label: "All Statuses", dotColor: T.olive },
    { value: "attended", label: "Attended", dotColor: "#22C55E" },
    { value: "missed", label: "Missed", dotColor: "#F59E0B" },
    { value: "not joined", label: "Not Joined", dotColor: "#71717A" }
  ], [dynamicTiers]);

  const formatISTTime = (joinedAtStr) => {
    if (!joinedAtStr || joinedAtStr === "N/A") return "N/A";
    if (joinedAtStr.includes("IST") || joinedAtStr.includes("ago")) return joinedAtStr;
    const d = new Date(joinedAtStr);
    if (isNaN(d.getTime())) return joinedAtStr;
    return d.toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    }) + " IST";
  };

  // Generate complete roster records for all tier subscribers across sessions
  const rosterReports = useMemo(() => {
    const records = [];
    if (!sessions || sessions.length === 0) return records;

    let targetSessions = sessions;
    if (rosterSessionFilter !== "all") {
      targetSessions = sessions.filter(s => String(s.id) === String(rosterSessionFilter));
    } else if (selectedDateStr) {
      targetSessions = sessions.filter(s => s.dateStr === selectedDateStr);
    } else {
      targetSessions = sessions.slice(0, 8); // default latest sessions
    }

    const seenKeys = new Set();

    targetSessions.forEach(s => {
      const sessionTier = s.tier || "Free";
      const sRank = getTierRank(sessionTier, dynamicPackages);

      let tierSubscribers = (allUsers || []).filter(u => {
        const uTier = u.subscriptionTier || u.tier || "Free";
        const uRank = getTierRank(uTier, dynamicPackages);
        return uRank >= sRank;
      });

      const rosterList = s.rosterDetails || [];
      const userMap = new Map();

      tierSubscribers.forEach(u => {
        const key = (u.email || u.id || u.userId || "").toLowerCase().trim();
        if (key) userMap.set(key, u);
      });

      rosterList.forEach(r => {
        const key = (r.email || r.userId || r.id || "").toLowerCase().trim();
        if (key && !userMap.has(key)) {
          userMap.set(key, {
            id: r.userId || r.id,
            name: r.fullName || r.name || "Student User",
            email: r.email,
            tier: r.tier || s.tier || "Free",
            isRegistered: true
          });
        }
      });

      const baseList = Array.from(userMap.values());
      const registeredCount = s.registered || 0;
      const attendedCount = Math.min(rosterList.length, registeredCount > 0 ? registeredCount : rosterList.length);
      const missedCount = Math.max(0, registeredCount - attendedCount);

      baseList.forEach((u, idx) => {
        const uEmail = (u.email || "").toLowerCase().trim();
        const uId = String(u.id || u.userId || "").toLowerCase().trim();

        const matchedRoster = rosterList.find(r => {
          const rEmail = (r.email || "").toLowerCase().trim();
          const rId = String(r.userId || r.id || "").toLowerCase().trim();
          if (uEmail && rEmail && uEmail === rEmail) return true;
          if (uId && rId && uId === rId) return true;
          return false;
        });

        let status = "Not Joined";
        let watchMins = 0;
        let joinedAt = "N/A";

        if (matchedRoster) {
          status = "Attended";
          watchMins = matchedRoster.watchMins != null ? matchedRoster.watchMins : (matchedRoster.watchMinutes != null ? matchedRoster.watchMinutes : 1);
          const realJoin = matchedRoster.joinedAt || matchedRoster.checkInTime;
          joinedAt = realJoin ? formatISTTime(realJoin) : "N/A";
        } else if (u.isRegistered || u.hasBooked) {
          status = "Missed";
        }

        const itemTier = u.subscriptionTier || u.tier || s.tier || "Free";
        const uniqueKey = `${s.id}-${u.id || idx}-${uEmail}`;

        if (!seenKeys.has(uniqueKey)) {
          seenKeys.add(uniqueKey);

          const matchesTier = rosterTierFilter === "all" || itemTier.toLowerCase() === rosterTierFilter.toLowerCase();
          const matchesStatus = rosterStatusFilter === "all" || status.toLowerCase() === rosterStatusFilter.toLowerCase();

          if (matchesTier && matchesStatus) {
            records.push({
              id: uniqueKey,
              sessionId: s.id,
              sessionTitle: s.title,
              tier: itemTier,
              status: status,
              duration: s.duration || 45,
              watchMins: watchMins,
              watchPct: status === "Attended" ? Math.round((watchMins / (s.duration || 45)) * 100) : 0,
              fullName: u.name || u.fullName || "Student User",
              email: u.email || "—",
              joinedAt: joinedAt,
              location: matchedRoster?.location || "—"
            });
          }
        }
      });
    });

    const STATUS_PRIORITY = {
      "attended": 1,
      "missed": 2,
      "not joined": 3,
      "not yet joined": 3
    };

    records.sort((a, b) => {
      const pA = STATUS_PRIORITY[(a.status || "").toLowerCase().trim()] || 99;
      const pB = STATUS_PRIORITY[(b.status || "").toLowerCase().trim()] || 99;
      if (pA !== pB) return pA - pB;
      return (a.fullName || "").localeCompare(b.fullName || "");
    });

    return records;
  }, [sessions, selectedDateStr, allUsers, rosterSessionFilter, rosterTierFilter, rosterStatusFilter]);

  const rosterPerPage = 10;
  const totalRosterPages = Math.ceil(rosterReports.length / rosterPerPage);
  const paginatedRoster = useMemo(() => {
    const startIndex = (rosterPage - 1) * rosterPerPage;
    return rosterReports.slice(startIndex, startIndex + rosterPerPage);
  }, [rosterReports, rosterPage]);

  // Reset page when date selection changes
  useEffect(() => {
    setRosterPage(1);
  }, [selectedDateStr]);

  const drawerSession = drawerId ? sessions.find(s => s.id===drawerId) : null;
  const openEdit  = (s) => { setEditSession(s); };
  const closeModal = () => { setShowCreate(false); setEditSession(null); };

  const liveCount      = sessions.filter(s => s.status==="Live").length;
  const upcomingCount  = sessions.filter(s => s.status==="Upcoming").length;
  const completedCount = sessions.filter(s => s.status==="Completed").length;
  const avgAttendance  = sessions.length ? Math.round(sessions.reduce((a,s)=>a+s.registered,0)/sessions.length) : 0;

  const handleExportScheduleCSV = () => {
    if (sessions.length === 0) {
      showToast("No sessions to export.");
      return;
    }
    const headers = ["ID", "Title", "Instructor", "Date", "Time", "Duration (mins)", "Tier", "Status", "Capacity", "Registered"];
    const rows = sessions.map(s => [
      s.id,
      `"${s.title.replace(/"/g, '""')}"`,
      `"${s.instructor.replace(/"/g, '""')}"`,
      s.date,
      `"${s.time.replace(/"/g, '""')}"`,
      s.duration,
      s.tier,
      s.status,
      s.capacity,
      s.registered
    ]);
    const csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "divyayoga_livesessions_schedule.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast("✓ Schedule exported successfully!");
  };

  return (
    <AdminDashboardLayout activeNav="sessions">
      <style>{`@keyframes blink{0%,100%{opacity:1}50%{opacity:.3}}`}</style>
      <div style={{ fontFamily:"'Inter',sans-serif" }}>
        <motion.div animate={{ y:[0,-18,0],x:[0,14,0] }} transition={{ duration:14,repeat:Infinity,ease:"easeInOut" }}
          className="fixed w-[460px] h-[460px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(107,138,90,0.07),transparent 70%)",filter:"blur(80px)",top:-140,right:-80,zIndex:0 }}/>
        <motion.div animate={{ y:[0,14,0],x:[0,-10,0] }} transition={{ duration:17,repeat:Infinity,ease:"easeInOut" }}
          className="fixed w-[340px] h-[340px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(242,154,12,0.06),transparent 70%)",filter:"blur(80px)",bottom:-80,left:40,zIndex:0 }}/>

        <div className="relative z-10 w-full min-w-0">
          {/* Topbar */}
          <motion.div initial={{ opacity:0,y:-16 }} animate={{ opacity:1,y:0 }} className="ad-toolbar flex items-center justify-between mb-6">
            <div>
              <h1 className="font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif",fontSize:26,letterSpacing:"-1px" }}>
                Live Session Scheduling
              </h1>
              <p className="text-[12px] mt-1" style={{ color:T.muted }}>
                {sessions.length} total · {liveCount} live now · {upcomingCount} upcoming
              </p>
            </div>
            <div className="flex gap-2 flex-wrap justify-end">
              <motion.button whileHover={{ y:-1 }} whileTap={{ scale:0.97 }}
                onClick={() => setShowClearAllConfirm(true)}
                disabled={clearingAll || loading}
                className="h-9 px-4 rounded-full text-[12px] font-semibold flex items-center gap-1.5"
                style={{
                  background:"rgba(226,75,74,0.12)",
                  border:"1px solid rgba(226,75,74,0.3)",
                  color:T.red,
                  cursor: clearingAll ? "not-allowed" : "pointer",
                  opacity: clearingAll ? 0.6 : 1,
                  fontFamily:"'Inter',sans-serif"
                }}>
                <FiTrash2 size={13}/> {clearingAll ? "Deleting…" : "Delete All Session Data"}
              </motion.button>
              <motion.button whileHover={{ y:-1 }} onClick={handleExportScheduleCSV}
                className="h-9 px-4 rounded-full text-[12px] font-semibold flex items-center gap-1.5"
                style={{ background:"rgba(255,255,255,0.9)",border:`1.5px solid ${T.border}`,color:T.mid,cursor:"pointer",fontFamily:"'Inter',sans-serif" }}>
                <FiDownload size={13}/> Export
              </motion.button>
              <motion.button whileHover={{ y:-2 }} whileTap={{ scale:0.97 }} onClick={() => setShowCreate(true)}
                className="h-9 px-5 rounded-full text-white text-[12px] font-bold flex items-center gap-1.5"
                style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`,boxShadow:`0 5px 16px rgba(107,138,90,0.38)`,border:"none",cursor:"pointer",fontFamily:"'Inter',sans-serif" }}>
                <FiPlus size={13}/> New Session
              </motion.button>
            </div>
          </motion.div>

          {/* Stat strip */}
          <div className="ad-kpi grid grid-cols-5 gap-3 mb-5">
            <StatCard icon={FiCalendar}     value={sessions.length} label="Total Sessions" trend="This month" iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`} accentColor={`linear-gradient(90deg,${T.olive},${T.olive2})`} cardBg="linear-gradient(145deg,#F5FAF2,#F9FDF5)" delay={0.06}/>
            <StatCard icon={FiVideo}        value={liveCount}       label="Live Now"       trend="Active sessions" iconBg="rgba(34,197,94,0.2)" accentColor={T.green} delay={0.1}/>
            <StatCard icon={FiClock}        value={upcomingCount}   label="Upcoming"       trend="Next 7 days" iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`} accentColor={`linear-gradient(90deg,${T.orange},${T.orange2})`} cardBg="linear-gradient(145deg,#FFF4E8,#FFF8EE)" delay={0.14}/>
            <StatCard icon={FiCheckCircle}  value={completedCount}  label="Completed"      trend="All time" iconBg="rgba(83,74,183,0.15)" accentColor="#534AB7" delay={0.18}/>
            <StatCard icon={FiUsers}        value={avgAttendance}   label="Avg Attendance" trend="Per session" iconBg="rgba(24,95,165,0.15)" accentColor={T.blue} delay={0.22}/>
          </div>

          {/* Main grid calendar + list */}
          <div className="ad-1col grid gap-4" style={{ gridTemplateColumns:"300px 1fr" }}>
            {/* LEFT Calendar */}
            <motion.div initial={{ opacity:0,x:-16 }} animate={{ opacity:1,x:0 }} transition={{ delay:0.1,duration:0.6,ease:[0.22,1,0.36,1] }}>
              <MiniCalendar sessions={sessions} selectedDateStr={selectedDateStr} onDateClick={d => setSelectedDateStr(prev => prev===d?null:d)}/>
              <div className="rounded-[24px] p-5 mt-4" style={glass}>
                <h3 className="font-black text-[14px] text-[#1F1F1F] mb-4" style={{ fontFamily:"'Poppins',sans-serif" }}>
                  {selectedDateStr ? `Sessions ${selectedDateStr}` : "Today's Sessions"}
                </h3>
                {sessions.filter(s => selectedDateStr ? s.dateStr===selectedDateStr : s.status==="Live"||s.status==="Upcoming").slice(0,3).map((s,i) => (
                  <motion.div key={s.id} whileHover={{ x:3 }} onClick={() => setDrawerID(s.id)}
                    className="flex items-center gap-3 p-3 rounded-[14px] mb-2 cursor-pointer border"
                    style={{ background:"rgba(255,255,255,0.7)",borderColor:T.border,transition:"all 0.22s" }}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=`rgba(107,138,90,0.3)`;e.currentTarget.style.background="rgba(255,255,255,0.95)"}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.background="rgba(255,255,255,0.7)"}}>
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background:STATUS_S[s.status]?.dot||T.olive }}/>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-bold text-[#1F1F1F] truncate">{s.title}</p>
                      <p className="text-[10px]" style={{ color:T.muted }}>{s.time} · {s.instructor}</p>
                    </div>
                    <StatusPill status={s.status}/>
                  </motion.div>
                ))}
                {sessions.filter(s => selectedDateStr ? s.dateStr===selectedDateStr : s.status==="Live"||s.status==="Upcoming").length===0 && (
                  <p className="text-[12px] text-center py-4" style={{ color:T.muted }}>No sessions for this date</p>
                )}
                {selectedDateStr && (
                  <motion.button whileHover={{ background:"#fff" }} onClick={() => setSelectedDateStr(null)}
                    className="w-full mt-2 h-9 rounded-full text-[12px] font-semibold flex items-center justify-center gap-1.5"
                    style={{ background:"rgba(255,255,255,0.9)",border:`1.5px solid ${T.border}`,color:T.muted,cursor:"pointer",fontFamily:"'Inter',sans-serif" }}>
                    <FiX size={12}/> Clear filter
                  </motion.button>
                )}
              </div>
            </motion.div>


            {/* RIGHT   Session list */}
            <motion.div initial={{ opacity:0,y:14 }} animate={{ opacity:1,y:0 }} transition={{ delay:0.14,duration:0.6 }}>
              <div className="ad-filters flex items-center gap-2.5 mb-4 flex-wrap">
                <div className="ad-filters-full flex items-center gap-2 rounded-[14px] px-3.5 py-2.5"
                  style={{ background:"rgba(255,255,255,0.82)",border:`1px solid ${T.border}` }}>
                  <FiSearch size={13} style={{ color:T.light }}/>
                  <input value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Search sessions…"
                    className="bg-transparent border-none outline-none text-[13px] text-[#1F1F1F]"
                    style={{ fontFamily:"'Inter',sans-serif",width:160 }}/>
                </div>
                <CustomDropdown
                  label="Status"
                  value={statusF}
                  onChange={setStatusF}
                  options={[
                    { value: "all", label: "All Statuses", dotColor: "#888" },
                    { value: "Live", label: "Live", dotColor: STATUS_S.Live.dot },
                    { value: "Upcoming", label: "Upcoming", dotColor: STATUS_S.Upcoming.dot },
                    { value: "Completed", label: "Completed", dotColor: STATUS_S.Completed.dot },
                    { value: "Cancelled", label: "Cancelled", dotColor: STATUS_S.Cancelled.dot },
                  ]}
                  icon={FiFilter}
                />
                <CustomDropdown
                  label="Tier"
                  value={tierF}
                  onChange={setTierF}
                  options={[
                    { value: "all", label: "All Tiers" },
                    ...dynamicTiers.map(t => ({ value: t, label: t }))
                  ]}
                  icon={FiFilter}
                />
                <span className="ad-filters-full text-[11px] ml-auto" style={{ color:T.muted }}>
                  {loading ? "Loading…" : `${filtered.length} session${filtered.length!==1?"s":""}`}
                </span>
              </div>

              <div className="ad-table rounded-[24px] overflow-hidden" style={glass}>
                <table style={{ width:"100%",borderCollapse:"collapse" }}>
                  <thead>
                    <tr>
                      {["Session","Date & Time","Instructor","Tier","Status","Registered","Actions"].map(h => (
                        <th key={h} style={{ fontSize:10,fontWeight:800,textTransform:"uppercase",letterSpacing:"0.9px",color:T.light,padding:"10px 14px",textAlign:h==="Actions"?"right":"left",borderBottom:`1px solid rgba(239,229,216,0.6)`,whiteSpace:"nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={7} className="py-8">
                          <BrandLoader 
                            message="Loading Live Sessions..." 
                            subMessage="Syncing class schedules and instructor broadcasts" 
                            minHeight="min-h-[180px]" 
                          />
                        </td>
                      </tr>
                    ) : filtered.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-12">
                          <div className="w-12 h-12 rounded-[16px] flex items-center justify-center mx-auto mb-3" style={{ background:"rgba(107,138,90,0.1)" }}>
                            <FiCalendar size={20} style={{ color:T.olive }}/>
                          </div>
                          <p className="text-[14px] font-semibold text-[#1F1F1F]">No sessions found</p>
                          <p className="text-[12px] mt-1" style={{ color:T.muted }}>Try adjusting your filters</p>
                        </td>
                      </tr>
                    ) : paginatedSessions.map((s,i) => {
                      const fillPct = Math.round(s.registered/s.capacity*100);
                      const isSelectedRoster = String(rosterSessionFilter) === String(s.id);
                      return (
                        <motion.tr key={s.id} initial={{ opacity:0,x:-6 }} animate={{ opacity:1,x:0 }} transition={{ delay:i*0.04 }}
                          onClick={() => handleSelectSessionRow(s, false)} className="cursor-pointer transition-colors"
                          style={{
                            background: isSelectedRoster ? "rgba(107,138,90,0.1)" : "transparent",
                          }}
                          onMouseEnter={e => { if (!isSelectedRoster) e.currentTarget.style.background="rgba(107,138,90,0.04)"; }}
                          onMouseLeave={e => { if (!isSelectedRoster) e.currentTarget.style.background="transparent"; }}>
                          <td style={{ padding:"12px 14px",borderBottom:`1px solid rgba(239,229,216,0.35)` }}>
                            <div className="flex items-center gap-2">
                              {isSelectedRoster && <span className="w-2 h-2 rounded-full bg-[#6B8A5A] animate-ping flex-shrink-0" />}
                              <div>
                                <p className="text-[13px] font-bold text-[#1F1F1F]">{s.title}</p>
                                <p className="text-[11px] mt-0.5" style={{ color:T.muted }}>{s.duration} min session</p>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding:"12px 14px",borderBottom:`1px solid rgba(239,229,216,0.35)` }}>
                            <p className="text-[13px] font-semibold text-[#1F1F1F]">{s.date}</p>
                            <p className="text-[11px]" style={{ color:T.muted }}>{s.time}</p>
                          </td>
                          <td style={{ padding:"12px 14px",borderBottom:`1px solid rgba(239,229,216,0.35)`,fontSize:12,color:T.mid }}>{s.instructor}</td>
                          <td style={{ padding:"12px 14px",borderBottom:`1px solid rgba(239,229,216,0.35)` }}><TierPill tier={s.tier}/></td>
                          <td style={{ padding:"12px 14px",borderBottom:`1px solid rgba(239,229,216,0.35)` }}><StatusPill status={s.status}/></td>
                          <td style={{ padding:"12px 14px",borderBottom:`1px solid rgba(239,229,216,0.35)` }}>
                            <p className="text-[13px] font-bold text-[#1F1F1F]">{s.registered}/{s.capacity}</p>
                            <div className="h-[3px] rounded-full mt-1 overflow-hidden" style={{ width:60,background:"rgba(239,229,216,0.6)" }}>
                              <div className="h-full rounded-full" style={{ width:`${fillPct}%`,background:fillPct>=90?`linear-gradient(90deg,${T.red},#F87171)`:`linear-gradient(90deg,${T.olive},${T.olive2})` }}/>
                            </div>
                          </td>
                          <td style={{ padding:"12px 14px",borderBottom:`1px solid rgba(239,229,216,0.35)` }}>
                            <div className="flex items-center gap-1.5 justify-end">
                              {(() => {
                                if (s.status !== "Live" && s.status !== "Upcoming") return null;
                                const launchStatus = getLaunchTimeStatus(s);

                                if (launchStatus.canLaunch) {
                                  return (
                                    <motion.a
                                      href={s.meetLink || `/live-room/${s.id}`}
                                      onClick={(e) => e.stopPropagation()}
                                      whileHover={{ scale: 1.04 }}
                                      whileTap={{ scale: 0.96 }}
                                      className="px-3 py-1.5 rounded-full text-white text-[11px] font-extrabold flex items-center gap-1.5 border-none cursor-pointer shadow-sm no-underline shrink-0"
                                      style={{
                                        background: "linear-gradient(135deg, #EF4444, #DC2626)",
                                        boxShadow: "0 4px 12px rgba(239, 68, 68, 0.35)",
                                        fontFamily: "'Inter', sans-serif"
                                      }}
                                      title="Launch Live Studio / Start Class"
                                    >
                                      <FiRadio size={12} className="animate-pulse" />
                                      <span>Launch Class</span>
                                    </motion.a>
                                  );
                                } else {
                                  return (
                                    <motion.button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEarlyLaunchSession(s);
                                      }}
                                      whileHover={{ scale: 1.02 }}
                                      whileTap={{ scale: 0.98 }}
                                      className="px-2.5 py-1.5 rounded-full text-zinc-600 text-[10.5px] font-bold flex items-center gap-1.5 border border-zinc-300/80 bg-zinc-100 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-900 cursor-pointer shrink-0 transition-colors"
                                      title={launchStatus.message}
                                    >
                                      <FiClock size={11} className="text-zinc-500" />
                                      <span>Starts in {launchStatus.startsIn || "Future"}</span>
                                    </motion.button>
                                  );
                                }
                              })()}
                              {[
                                {
                                  icon: FiUsers,
                                  onClick: e => {
                                    e.stopPropagation();
                                    handleSelectSessionRow(s, true);
                                  },
                                  tip: "Sync & Jump to Student Roster",
                                  danger: false,
                                  customColor: "#185FA5"
                                },
                                {
                                  icon: FiCopy,
                                  onClick: e => {
                                    e.stopPropagation();
                                    const link = `${window.location.origin}/live-room/${s.id}`;
                                    navigator.clipboard.writeText(link);
                                    showToast("✓ Live Session link copied to clipboard!");
                                  },
                                  tip: "Copy Join Link",
                                  danger: false,
                                  customColor: "#E97818"
                                },
                                {
                                  icon: FaWhatsapp,
                                  onClick: e => {
                                    e.stopPropagation();
                                    const link = `${window.location.origin}/live-room/${s.id}`;
                                    const text = `*Join Live Yoga Session: ${s.title}*\n📅 *Date & Time:* ${s.date} at ${s.time}\n👨‍🏫 *Instructor:* ${s.instructor}\n👉 *Click Link to Join:* ${link}`;
                                    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, "_blank");
                                  },
                                  tip: "Share on WhatsApp",
                                  danger: false,
                                  customColor: "#22C55E"
                                },
                                { icon:FiEdit3,  onClick:e=>{e.stopPropagation();openEdit(s)},           tip:"Edit",   danger:false },
                                { icon:FiBell,   onClick:e=>{e.stopPropagation();handleNotifyUsers(s);}, tip:"Send Session Reminder", danger:false, customColor: "#6B8A5A" },
                                { icon:FiTrash2, onClick:e=>promptDeleteSession(s,e), tip:"Delete Session", danger:true  },
                              ].map(({ icon:Icon,onClick,tip,danger,customColor }) => (
                                <motion.button key={tip} whileHover={{ y:-1 }} whileTap={{ scale:0.9 }}
                                  onClick={onClick} title={tip}
                                  className="w-7 h-7 rounded-[9px] flex items-center justify-center border cursor-pointer"
                                  style={{ background:"rgba(255,255,255,0.9)",borderColor:danger?"rgba(226,75,74,0.2)":T.border,color:customColor ? customColor : (danger?T.red:"#888"),transition:"all 0.2s" }}>
                                  <Icon size={13}/>
                                </motion.button>
                              ))}
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
                {/* Sessions Table Pagination — User Management Style */}
                <div
                  className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t"
                  style={{ borderColor: "rgba(239,229,216,0.6)", background: "rgba(255,255,255,0.4)" }}
                >
                  <div className="text-[12px] font-bold text-zinc-500">
                    Showing <span className="text-zinc-900">{filtered.length > 0 ? (sessionsPage - 1) * sessionsPerPage + 1 : 0}</span> to{" "}
                    <span className="text-zinc-900">{Math.min(sessionsPage * sessionsPerPage, filtered.length)}</span> of{" "}
                    <span className="text-zinc-900">{filtered.length}</span> sessions
                  </div>

                  {totalSessionsPages > 1 && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[11.5px] font-black text-amber-900 bg-amber-100/90 px-3 py-1 rounded-full border border-amber-300 shadow-2xs mr-1">
                        Page {sessionsPage} of {totalSessionsPages}
                      </span>

                      <motion.button
                        whileTap={{ scale: 0.92 }}
                        disabled={sessionsPage <= 1}
                        onClick={() => setSessionsPage(1)}
                        title="First Page"
                        className="w-8 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center justify-center cursor-pointer transition-all shadow-2xs"
                      >
                        <FiChevronsLeft size={14} />
                      </motion.button>

                      <motion.button
                        whileTap={{ scale: 0.92 }}
                        disabled={sessionsPage <= 1}
                        onClick={() => setSessionsPage((p) => Math.max(1, p - 1))}
                        title="Previous Page"
                        className="px-2.5 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
                      >
                        <FiChevronLeft size={14} /> Prev
                      </motion.button>

                      <div className="flex items-center gap-1">
                        {(() => {
                          let pages = [];
                          if (totalSessionsPages <= 7) {
                            pages = Array.from({ length: totalSessionsPages }, (_, i) => i + 1);
                          } else if (sessionsPage <= 4) {
                            pages = [1, 2, 3, 4, 5, "...", totalSessionsPages];
                          } else if (sessionsPage >= totalSessionsPages - 3) {
                            pages = [1, "...", totalSessionsPages - 4, totalSessionsPages - 3, totalSessionsPages - 2, totalSessionsPages - 1, totalSessionsPages];
                          } else {
                            pages = [1, "...", sessionsPage - 1, sessionsPage, sessionsPage + 1, "...", totalSessionsPages];
                          }

                          return pages.map((item, idx) => {
                            if (item === "...") {
                              return (
                                <span key={`dots-${idx}`} className="px-1 text-zinc-400 font-black text-[12px] select-none">
                                  •••
                                </span>
                              );
                            }

                            const isActive = sessionsPage === item;
                            return (
                              <motion.button
                                key={item}
                                whileTap={{ scale: 0.92 }}
                                onClick={() => setSessionsPage(item)}
                                className={`w-8 h-8 rounded-lg text-[12px] font-black transition-all cursor-pointer ${
                                  isActive
                                    ? "text-white shadow-xs"
                                    : "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-100"
                                }`}
                                style={
                                  isActive
                                    ? {
                                        background: `linear-gradient(135deg,${T.olive},${T.olive2})`,
                                        borderColor: "transparent",
                                        boxShadow: `0 4px 12px rgba(107,138,90,0.35)`,
                                      }
                                    : {}
                                }
                              >
                                {item}
                              </motion.button>
                            );
                          });
                        })()}
                      </div>

                      <motion.button
                        whileTap={{ scale: 0.92 }}
                        disabled={sessionsPage >= totalSessionsPages}
                        onClick={() => setSessionsPage((p) => Math.min(totalSessionsPages, p + 1))}
                        title="Next Page"
                        className="px-2.5 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
                      >
                        Next <FiChevronRight size={14} />
                      </motion.button>

                      <motion.button
                        whileTap={{ scale: 0.92 }}
                        disabled={sessionsPage >= totalSessionsPages}
                        onClick={() => setSessionsPage(totalSessionsPages)}
                        title="Last Page"
                        className="w-8 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center justify-center cursor-pointer transition-all shadow-2xs"
                      >
                        <FiChevronsRight size={14} />
                      </motion.button>
                    </div>
                  )}
                </div>
              </div>

              {/* Roster & Attendance Report Table */}
              <div ref={rosterTableRef} className="mt-8 rounded-[24px] overflow-hidden shadow-sm border border-zinc-200 bg-white/60 backdrop-blur-md transition-all">
                <div className="px-6 py-4 border-b flex flex-col md:flex-row items-start md:items-center justify-between gap-3 bg-white/20" style={{ borderColor:"rgba(239,229,216,0.6)" }}>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-black text-[14px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>
                        {selectedDateStr ? `Student Tier Roster (${selectedDateStr})` : "Student Tier Attendance Roster"}
                      </h3>
                      {selectedSessionObj && (
                        <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-500/10 text-amber-800 border border-amber-500/20 text-[10.5px] font-bold">
                          <span>Session: <strong>{selectedSessionObj.title}</strong></span>
                          <button
                            type="button"
                            onClick={() => { setRosterSessionFilter("all"); setRosterPage(1); }}
                            className="text-amber-700 hover:text-amber-900 bg-transparent border-none cursor-pointer p-0 ml-0.5 font-extrabold flex items-center"
                            title="Clear session filter to view all sessions"
                          >
                            <FiX size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-[10px] mt-0.5" style={{ color:T.muted }}>
                      Showing tier membership attendance statuses (Attended, Missed, Not Joined) and location analytics
                    </p>
                  </div>
                  <div className="ad-filters ad-full flex flex-wrap items-center gap-2">
                    <CustomDropdown
                      options={rosterSessionOptions}
                      value={rosterSessionFilter}
                      onChange={(val) => { setRosterSessionFilter(val); setRosterPage(1); }}
                      placeholder="All Live Sessions"
                    />
                    <CustomDropdown
                      options={rosterTierOptions}
                      value={rosterTierFilter}
                      onChange={(val) => { setRosterTierFilter(val); setRosterPage(1); }}
                      placeholder="All Package Tiers"
                    />
                    <CustomDropdown
                      options={rosterStatusOptions}
                      value={rosterStatusFilter}
                      onChange={(val) => { setRosterStatusFilter(val); setRosterPage(1); }}
                      placeholder="All Statuses"
                    />
                    <span className="text-[11px] font-bold px-3 py-2 rounded-[14px] bg-[#6B8A5A]/10 text-[#6B8A5A] border border-[#6B8A5A]/20 flex items-center justify-center">
                      {rosterReports.length} Records
                    </span>
                  </div>
                </div>
                <table className="ad-table-self" style={{ width:"100%",borderCollapse:"collapse" }}>
                  <thead>
                    <tr>
                      {["Student & Email","Tier Access","Session Title","Attendance Status","Check-In Time (IST)","Watch Duration","Place","Country"].map(h => (
                        <th key={h} style={{ fontSize:10,fontWeight:800,textTransform:"uppercase",letterSpacing:"0.9px",color:T.light,padding:"10px 14px",textAlign:"left",borderBottom:`1px solid rgba(239,229,216,0.6)`,whiteSpace:"nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRoster.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-12">
                          <div className="w-12 h-12 rounded-[16px] flex items-center justify-center mx-auto mb-3" style={{ background:"rgba(107,138,90,0.1)" }}>
                            <FiUsers size={20} style={{ color:T.olive }}/>
                          </div>
                          <p className="text-[14px] font-semibold text-[#1F1F1F]">No student tier records found</p>
                          <p className="text-[12px] mt-1" style={{ color:T.muted }}>Try adjusting session, tier, or status filter criteria</p>
                        </td>
                      </tr>
                    ) : paginatedRoster.map((user, i) => {
                      const { place, country } = parseAddressDetails(user.location);
                      
                      return (
                        <motion.tr key={user.id} initial={{ opacity:0,x:-6 }} animate={{ opacity:1,x:0 }} transition={{ delay:i*0.04 }}
                          className="hover:bg-zinc-500/5 transition-colors">
                          <td style={{ padding:"12px 14px",borderBottom:`1px solid rgba(239,229,216,0.2)` }}>
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-md flex items-center justify-center text-white text-[9px] font-black"
                                style={{ background:`linear-gradient(135deg,${[T.olive,T.orange,"#534AB7","#185FA5","#E24B4A"][i%5]},${[T.olive2,T.orange2,"#7F77DD","#378ADD","#F87171"][i%5]})` }}>
                                {user.fullName.split(" ").map(w=>w[0]).join("")}
                              </div>
                              <div>
                                <p className="text-[12px] font-bold text-[#1F1F1F]">{user.fullName}</p>
                                <p className="text-[9px] text-zinc-400">{user.email}</p>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding:"12px 14px",borderBottom:`1px solid rgba(239,229,216,0.2)` }}>
                            <TierPill tier={user.tier} />
                          </td>
                          <td style={{ padding:"12px 14px",borderBottom:`1px solid rgba(239,229,216,0.2)`,fontSize:12,color:T.mid }}>
                            {user.sessionTitle}
                          </td>
                          <td style={{ padding:"12px 14px",borderBottom:`1px solid rgba(239,229,216,0.2)` }}>
                            {user.status === "Attended" && (
                              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-700 border border-emerald-500/20">
                                Attended
                              </span>
                            )}
                            {user.status === "Missed" && (
                              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/10 text-amber-700 border border-amber-500/20">
                                Missed
                              </span>
                            )}
                            {user.status === "Not Joined" && (
                              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-zinc-500/10 text-zinc-600 border border-zinc-500/20">
                                Not Joined
                              </span>
                            )}
                          </td>
                          <td style={{ padding:"12px 14px",borderBottom:`1px solid rgba(239,229,216,0.2)`,fontSize:11,color:T.muted }}>
                            {user.joinedAt}
                          </td>
                          <td style={{ padding:"12px 14px",borderBottom:`1px solid rgba(239,229,216,0.2)` }}>
                            <div className="flex flex-col gap-1">
                              <span className={`text-[11px] font-bold ${user.status === "Attended" ? "text-emerald-700" : "text-zinc-400"}`}>
                                {user.watchMins} / {user.duration} mins ({user.watchPct}%)
                              </span>
                              <div className="w-24 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${user.watchPct}%`, background: user.status === "Attended" ? `linear-gradient(90deg, ${T.olive}, ${T.green})` : "#D4D4D8" }} />
                              </div>
                            </div>
                          </td>
                          <td style={{ padding:"12px 14px",borderBottom:`1px solid rgba(239,229,216,0.2)`,fontSize:12,color:T.mid }}>
                            {place}
                          </td>
                          <td style={{ padding:"12px 14px",borderBottom:`1px solid rgba(239,229,216,0.2)`,fontSize:12,color:T.mid }}>
                            {country}
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
                {/* Roster Pagination Controls — User Management Style */}
                <div
                  className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t"
                  style={{ borderColor: "rgba(239,229,216,0.6)", background: "rgba(255,255,255,0.4)" }}
                >
                  <div className="text-[12px] font-bold text-zinc-500">
                    Showing <span className="text-zinc-900">{rosterReports.length > 0 ? (rosterPage - 1) * rosterPerPage + 1 : 0}</span> to{" "}
                    <span className="text-zinc-900">{Math.min(rosterPage * rosterPerPage, rosterReports.length)}</span> of{" "}
                    <span className="text-zinc-900">{rosterReports.length}</span> attendees
                  </div>

                  {totalRosterPages > 1 && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[11.5px] font-black text-amber-900 bg-amber-100/90 px-3 py-1 rounded-full border border-amber-300 shadow-2xs mr-1">
                        Page {rosterPage} of {totalRosterPages}
                      </span>

                      <motion.button
                        whileTap={{ scale: 0.92 }}
                        disabled={rosterPage <= 1}
                        onClick={() => setRosterPage(1)}
                        title="First Page"
                        className="w-8 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center justify-center cursor-pointer transition-all shadow-2xs"
                      >
                        <FiChevronsLeft size={14} />
                      </motion.button>

                      <motion.button
                        whileTap={{ scale: 0.92 }}
                        disabled={rosterPage <= 1}
                        onClick={() => setRosterPage((p) => Math.max(1, p - 1))}
                        title="Previous Page"
                        className="px-2.5 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
                      >
                        <FiChevronLeft size={14} /> Prev
                      </motion.button>

                      <div className="flex items-center gap-1">
                        {(() => {
                          let pages = [];
                          if (totalRosterPages <= 7) {
                            pages = Array.from({ length: totalRosterPages }, (_, i) => i + 1);
                          } else if (rosterPage <= 4) {
                            pages = [1, 2, 3, 4, 5, "...", totalRosterPages];
                          } else if (rosterPage >= totalRosterPages - 3) {
                            pages = [1, "...", totalRosterPages - 4, totalRosterPages - 3, totalRosterPages - 2, totalRosterPages - 1, totalRosterPages];
                          } else {
                            pages = [1, "...", rosterPage - 1, rosterPage, rosterPage + 1, "...", totalRosterPages];
                          }

                          return pages.map((item, idx) => {
                            if (item === "...") {
                              return (
                                <span key={`dots-${idx}`} className="px-1 text-zinc-400 font-black text-[12px] select-none">
                                  •••
                                </span>
                              );
                            }

                            const isActive = rosterPage === item;
                            return (
                              <motion.button
                                key={item}
                                whileTap={{ scale: 0.92 }}
                                onClick={() => setRosterPage(item)}
                                className={`w-8 h-8 rounded-lg text-[12px] font-black transition-all cursor-pointer ${
                                  isActive
                                    ? "text-white shadow-xs"
                                    : "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-100"
                                }`}
                                style={
                                  isActive
                                    ? {
                                        background: `linear-gradient(135deg,${T.olive},${T.olive2})`,
                                        borderColor: "transparent",
                                        boxShadow: `0 4px 12px rgba(107,138,90,0.35)`,
                                      }
                                    : {}
                                }
                              >
                                {item}
                              </motion.button>
                            );
                          });
                        })()}
                      </div>

                      <motion.button
                        whileTap={{ scale: 0.92 }}
                        disabled={rosterPage >= totalRosterPages}
                        onClick={() => setRosterPage((p) => Math.min(totalRosterPages, p + 1))}
                        title="Next Page"
                        className="px-2.5 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
                      >
                        Next <FiChevronRight size={14} />
                      </motion.button>

                      <motion.button
                        whileTap={{ scale: 0.92 }}
                        disabled={rosterPage >= totalRosterPages}
                        onClick={() => setRosterPage(totalRosterPages)}
                        title="Last Page"
                        className="w-8 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center justify-center cursor-pointer transition-all shadow-2xs"
                      >
                        <FiChevronsRight size={14} />
                      </motion.button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        <AnimatePresence>
          {drawerSession && (
            <SessionDrawer session={drawerSession} onClose={() => setDrawerID(null)}
              showToast={showToast}
              allUsers={allUsers}
              dynamicPackages={dynamicPackages}
              onNotify={handleNotifyUsers}
              onEarlyLaunch={(s) => setEarlyLaunchSession(s)}
              onEdit={(s) => { setDrawerID(null); openEdit(s); }}
              onDeleteRequest={(s) => promptDeleteSession(s)}/>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {earlyLaunchSession && (
            <EarlyLaunchWarningModal
              session={earlyLaunchSession}
              onClose={() => setEarlyLaunchSession(null)}
              onEdit={(s) => {
                setEarlyLaunchSession(null);
                openEdit(s);
              }}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {(showCreate || editSession) && (
            <SessionModal session={editSession} onClose={closeModal}
              existingSessions={sessions}
              onSave={() => { closeModal(); fetchSessions(); }}
              showToast={showToast}/>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {deleteConfirmSession && (
            <ConfirmModal
              isOpen={!!deleteConfirmSession}
              title="Delete Live Session?"
              message={`Are you sure you want to permanently delete "${deleteConfirmSession.title}"? This will cancel the class and remove it from student schedules.`}
              confirmText="Delete Session"
              cancelText="Keep Session"
              isDanger={true}
              onConfirm={handleConfirmDelete}
              onCancel={() => setDeleteConfirmSession(null)}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showClearAllConfirm && (
            <ConfirmModal
              isOpen={showClearAllConfirm}
              title="Delete all session data?"
              message="This permanently deletes every row in LiveSessions, plus related attendance and signal rows. The tables stay. This cannot be undone."
              confirmText="Delete All Session Data"
              cancelText="Keep Data"
              isDanger={true}
              onConfirm={handleClearAllSessionData}
              onCancel={() => setShowClearAllConfirm(false)}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {toast && (
            <motion.div key={toast} initial={{ opacity:0,y:16,scale:0.95 }} animate={{ opacity:1,y:0,scale:1 }} exit={{ opacity:0,y:16 }}
              className="ad-toast fixed bottom-7 right-7 z-[300] flex items-center gap-3 px-5 py-3.5 rounded-[16px] text-white text-[13px] font-semibold"
              style={{ background:"linear-gradient(135deg,#1F1F1F,#2D2D2D)",boxShadow:"0 16px 48px rgba(0,0,0,0.25)",fontFamily:"'Inter',sans-serif" }}>
              <FiCheckCircle size={16} style={{ color:T.olive }}/>{toast}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AdminDashboardLayout>
  );
}